// kernel/useCanvas.ts
import { useCallback, useMemo } from 'react';
import { findBestDimensions } from '#lib/canvas-utils';
import { getTimelineDockBottomInsetPx } from '#lib/studio-viewport';
import { KernelHandle, useCanvasReturnType, Rect, Layer, Workspace, OriginalImage, GridOverlayOptions, ImageExportRenderOptions, MaskInterpretation, StreetCuts, RecastZone } from  '#types/types';
import {
  collectVisibleImageExportLayers,
  createPdfExportFile,
  hasVisibleTimelineMedia,
  isNativeSvgExportAvailable,
  renderSourceImageExport,
} from '#lib/image-export';
import {
  drawWorkspaceAnnotationOverlay,
  resolveWorkspaceAnnotationMask,
} from '#lib/workspace-annotations';
import { requireWorkspaceProjectIdFromKernel } from '#lib/workspace-scope';
import { rememberedStreetCuts, rememberStreetCuts, setStreetSeam, streetSeamBands } from '#lib/modelist/projectScene';
import { isStreetCuts, isStreetPanoramaFrame, STREET_DEVELOP_RECIPES, uncutStreet } from '#lib/modelist/streetDevelop';
import { isRecastZones, rememberedRecastZones, rememberRecastZones } from '#lib/recastExtras';
import { toast } from 'sonner';
import { getProjectPanOffset, getProjectSelectedLayersId, getProjectZoomLevel } from '#lib/project-runtime-layer-ui-state';
import { buildNativeSvgDocument, buildNativeSvgLayerFragment, loadSvgImageElement, resolveSvgLayerInlineMarkup } from '#lib/svg-layer';
import { getDrawingAwareSvgLayerSource, getDrawingLayerPayload, getDrawingLayerSvgSource } from '#lib/drawing-layer';
import {
  drawImageWithStudioLayerTransform,
  getTransformedRectBounds,
  projectStudioLayerLocalRectToWorld,
  resolveStudioLayerTransform,
} from '#lib/studio-layer-transform';
import { resolveStillRenderTimelineWindow, toFiniteTimelineMs } from '#lib/workspace-timeline';
import { mergeLayersForRender } from '#lib/layer-render-order';
import { resolveRasterLayerScale, resolveRasterLayerWorldSize } from '#lib/layer-geometry';
import { buildPrintSnapshot, loadPrintIccProfile } from '#lib/print-export';
import { runPrintExport } from '#lib/print-export-client';
import { signImageExport } from '#lib/provenance/signImageExport';
import anime from 'animejs';

interface useCanvasProps {
  kernel: React.RefObject<KernelHandle | null>;
}

const roundDownToMultipleOf = (number: number, multiple: number): number => {
  return Math.floor(number / multiple) * multiple;
};

const waitForVideoMetadata = (videoElement: HTMLVideoElement): Promise<void> => {
  if (videoElement.readyState >= 1) return Promise.resolve();
  return new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const cleanup = () => {
      videoElement.removeEventListener('loadedmetadata', handleReady);
      videoElement.removeEventListener('error', handleError);
      if (timeoutId) clearTimeout(timeoutId);
    };
    const handleReady = () => {
      cleanup();
      resolve();
    };
    const handleError = () => {
      cleanup();
      reject(new Error('Failed to load video metadata for still export.'));
    };
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('Timed out loading video metadata for still export.'));
    }, 5000);
    videoElement.addEventListener('loadedmetadata', handleReady, { once: true });
    videoElement.addEventListener('error', handleError, { once: true });
  });
};

const waitForVideoFrame = (videoElement: HTMLVideoElement): Promise<void> => {
  if (videoElement.readyState >= 2 && !videoElement.seeking) return Promise.resolve();
  return new Promise((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const frameVideoElement = videoElement as HTMLVideoElement & {
      requestVideoFrameCallback?: (callback: () => void) => number;
      cancelVideoFrameCallback?: (id: number) => void;
    };
    let frameCallbackId: number | null = null;
    const cleanup = () => {
      videoElement.removeEventListener('seeked', handleReady);
      videoElement.removeEventListener('loadeddata', handleReady);
      if (timeoutId) clearTimeout(timeoutId);
      if (frameCallbackId !== null && typeof frameVideoElement.cancelVideoFrameCallback === 'function') {
        frameVideoElement.cancelVideoFrameCallback(frameCallbackId);
      }
    };
    const handleReady = () => {
      cleanup();
      resolve();
    };
    timeoutId = setTimeout(handleReady, 1500);
    videoElement.addEventListener('seeked', handleReady, { once: true });
    videoElement.addEventListener('loadeddata', handleReady, { once: true });
    if (typeof frameVideoElement.requestVideoFrameCallback === 'function') {
      frameCallbackId = frameVideoElement.requestVideoFrameCallback(handleReady);
    }
  });
};

const seekVideoElementForStillRender = async (
  videoElement: HTMLVideoElement,
  targetTimeSec: number
): Promise<void> => {
  await waitForVideoMetadata(videoElement);
  const durationSec = Number.isFinite(videoElement.duration) && videoElement.duration > 0
    ? videoElement.duration
    : Number.POSITIVE_INFINITY;
  const boundedTargetSec = Number.isFinite(durationSec)
    ? Math.min(Math.max(0, targetTimeSec), Math.max(0, durationSec - 0.001))
    : Math.max(0, targetTimeSec);

  try {
    videoElement.pause();
  } catch {
    // Best effort; drawing the decoded frame is more important than playback state.
  }

  if (Math.abs(videoElement.currentTime - boundedTargetSec) > 1 / 60 || videoElement.readyState < 2) {
    await new Promise<void>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const cleanup = () => {
        videoElement.removeEventListener('seeked', handleSeeked);
        videoElement.removeEventListener('loadeddata', handleSeeked);
        videoElement.removeEventListener('error', handleError);
        if (timeoutId) clearTimeout(timeoutId);
      };
      const handleSeeked = () => {
        cleanup();
        resolve();
      };
      const handleError = () => {
        cleanup();
        reject(new Error('Failed to seek video frame for still export.'));
      };
      timeoutId = setTimeout(() => {
        cleanup();
        resolve();
      }, 1500);
      videoElement.addEventListener('seeked', handleSeeked, { once: true });
      videoElement.addEventListener('loadeddata', handleSeeked, { once: true });
      videoElement.addEventListener('error', handleError, { once: true });
      try {
        videoElement.currentTime = boundedTargetSec;
      } catch (error) {
        cleanup();
        reject(error instanceof Error ? error : new Error('Video seek failed for still export.'));
      }
    });
  }

  await waitForVideoFrame(videoElement);
};

const createTemporaryStillRenderVideo = (source: string): HTMLVideoElement => {
  const videoElement = document.createElement('video');
  videoElement.crossOrigin = 'anonymous';
  videoElement.preload = 'auto';
  videoElement.muted = true;
  videoElement.playsInline = true;
  videoElement.src = source;
  videoElement.load();
  return videoElement;
};

const destroyTemporaryStillRenderVideo = (videoElement: HTMLVideoElement) => {
  videoElement.pause();
  videoElement.removeAttribute('src');
  videoElement.load();
};

type OutputImageFormat = 'webp' | 'png' | 'jpeg';

interface CalculatedFitRect extends Rect {
  shiftDx: number;
  shiftDy: number;
}

export const useCanvas = ({
    kernel
}: useCanvasProps) : useCanvasReturnType => {
  const listProjectWorkspaces = useCallback((projectIdInput: string): Workspace[] => {
    const k = kernel.current;
    if (!k) return [];
    const projectId = projectIdInput.trim();
    if (!projectId) return [];
    const inventory = k.getInventory();
    const project = inventory.Project.registry[projectId];
    const workspaceIds = Array.isArray(project?.workspaceIds)
      ? project.workspaceIds.filter((workspaceId): workspaceId is string => typeof workspaceId === 'string' && workspaceId.trim().length > 0)
      : [];
    return workspaceIds
      .map((workspaceId) => k.getWorkspace(workspaceId))
      .filter((workspace): workspace is Workspace => Boolean(workspace));
  }, [kernel]);

  const handleCanvasKeyDown = useCallback((event: KeyboardEvent) => {
    if (!kernel.current) return;

    const target = event.target as HTMLElement | null;
    const isEditingTextInput = Boolean(
      target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.getAttribute('role') === 'textbox'
      )
    );

    const activeTool = kernel.current.getActiveTool();
    const isUndoRedoShortcut = event.metaKey || event.ctrlKey;
    const normalizedKey = event.key.toLowerCase();

    if (isUndoRedoShortcut && !event.altKey && (normalizedKey === 'z' || normalizedKey === 'y')) {
      if (isEditingTextInput) return;
      event.preventDefault();
      event.stopPropagation();
      if (normalizedKey === 'y' || (normalizedKey === 'z' && event.shiftKey)) {
        kernel.current.redoLastAction();
      } else {
        kernel.current.undoLastAction();
      }
      return;
    }

    if (event.key === 'Escape') {
      if (activeTool) {
        kernel.current.logUserAction('Tool Deselected (Esc)', { active_tool_before: activeTool });
        kernel.current.setActiveTool(null);
      }
      return;
    }

    const isDeleteKey = event.key === 'Delete' || event.key === 'Backspace';
    if (isDeleteKey) {
      if (isEditingTextInput || activeTool !== null || event.metaKey || event.ctrlKey || event.altKey) return;
      const projectIdFromTarget =
        target instanceof Element
          ? target.closest('[data-project-id]')?.getAttribute('data-project-id')?.trim() ?? ''
          : '';
      const activeSessionId = kernel.current.getActiveChatSessionId();
      const projectId = projectIdFromTarget || (activeSessionId ? kernel.current.getLastProjectId(activeSessionId) ?? '' : '');
      if (!projectId) return;
      const selectedLayerIds = getProjectSelectedLayersId(kernel.current, projectId)
        .filter((layerId) => Boolean(kernel.current?.getLayerRegistry()[layerId]));
      if (selectedLayerIds.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      selectedLayerIds.forEach((layerId) => {
        kernel.current?.removeLayer(layerId);
      });
      kernel.current.logUserAction('Selected Layers Deleted With Keyboard', {
        projectId,
        layerIds: selectedLayerIds,
        count: selectedLayerIds.length,
      });
      return;
    }

    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      if (isEditingTextInput) return;
      event.preventDefault();
      const direction = event.key.replace('Arrow', '').toLowerCase() as 'up' | 'down' | 'left' | 'right';
      kernel.current.logUserAction('Workspace Navigated (Arrows)', { direction });
      const activeSessionId = kernel.current.getActiveChatSessionId();
      const projectId = activeSessionId ? kernel.current.getLastProjectId(activeSessionId) : null;
      if (!projectId) return;
      const workspaceId = kernel.current.getProjectUIWorkspaceId(projectId);
      if (!workspaceId) return;
      kernel.current.navigateWorkspaceVersion?.(direction, workspaceId);
      return;
    }

    const isIncreasing = event.key === '+';
    const isDecreasing = event.key === '-';

    if (!(isIncreasing || isDecreasing) || !activeTool || isEditingTextInput) {
      return;
    }

    event.preventDefault();
    const direction = isIncreasing ? 1 : -1;

    if (['eraser', 'pencil', 'cutter'].includes(activeTool)) {
      const layerToolWidth = kernel.current.getLayerToolWidth();
      let strength = 0;
      if (layerToolWidth < 8) strength = 1;
      else if (layerToolWidth < 16) strength = 2;
      else if (layerToolWidth < 32) strength = 4;
      else if (layerToolWidth < 64) strength = 8;
      else strength = 16;

      const newSize = Math.round(Math.max(1, layerToolWidth + strength * direction));
      kernel.current.logUserAction('Tool Size Changed', {
        tool: activeTool,
        new_size: newSize,
        previous_size: layerToolWidth,
        source: 'keyboard',
      });
      kernel.current.setLayerToolWidth(newSize);
    }
  }, [kernel]);

  const zoomToFitRect = useCallback((projectIdInput: string, rectToFit: Rect, unzoomFactor: number = 0.85): boolean => {
    if (!kernel.current?.canvasContainerRef.current) {
      console.warn("zoomToFitRect: canvasContainerRef is null");
      return false;
    }
    const projectId = projectIdInput.trim();
    if (!projectId) {
      console.warn('zoomToFitRect: Missing projectId.');
      return false;
    }
    const zoomLevel = getProjectZoomLevel(kernel.current, projectId);
    const panOffset = getProjectPanOffset(kernel.current, projectId);

    const boundingBoxWidth = rectToFit.w;
    const boundingBoxHeight = rectToFit.h;

    if (boundingBoxWidth <= 0 || boundingBoxHeight <= 0) {
      console.warn("zoomToFitRect: Invalid rect dimensions.");
      return false;
    }

    const containerWidth = kernel.current.canvasContainerRef.current.offsetWidth;
    const containerHeight = kernel.current.canvasContainerRef.current.offsetHeight - getTimelineDockBottomInsetPx(kernel.current.canvasContainerRef.current);
    // Kept mounted under its project's 3D scene, the canvas has no size: a fit there would store a zoom of zero.
    if (!(containerWidth > 0 && containerHeight > 0)) return false;

    let newZoomLevel = Math.min(containerWidth / boundingBoxWidth, containerHeight / boundingBoxHeight);
    newZoomLevel *= unzoomFactor;
    
    const bboxCenterX = rectToFit.x + boundingBoxWidth / 2;
    const bboxCenterY = rectToFit.y + boundingBoxHeight / 2;

    const panOffsetX = (containerWidth / 2) - (bboxCenterX * newZoomLevel);
    const panOffsetY = (containerHeight / 2) - (bboxCenterY * newZoomLevel);

    const zoomThreshold = 0.001;
    const panThreshold = 1;

    if (
      Math.abs(newZoomLevel - zoomLevel) < zoomThreshold &&
      Math.abs(panOffsetX - panOffset.x) < panThreshold &&
      Math.abs(panOffsetY - panOffset.y) < panThreshold
    ) {
      return false; // No change
    }

    kernel.current?.animateZoomAndPan(projectId, newZoomLevel, { x: panOffsetX, y: panOffsetY }, 200);
    return true; // Change occurred
  }, [kernel]);


  /**
   * Calculates the world-space bounding box for a given set of layers within a specific workspace context.
   *
   * ### IMPORTANT: Coordinate System & Layer Remapping ###
   * A layer's world position is NOT fixed. It depends on the rendering context.
   * - A layer's `rect` property stores its position relative to a workspace's `frameRect`.
   * - When a layer from a parent workspace is rendered as part of a child's context, its `rect` is
   *   dynamically "remapped" to be relative to the CHILD's `frameRect`, not its own.
   *
   * This function correctly implements this logic by using the `frameRect` of the provided `workspaceId`
   * as the base for all layer position calculations, regardless of each layer's individual `layerWorkspaceId`.
   *
   * @param workspaceId The ID of the workspace providing the rendering context.
   * @param layersToUse Optional. A specific array of layers to measure. If not provided, it defaults to all layers in the workspace's context (including ancestors).
   * @returns A bounding box { minX, minY, maxX, maxY } in world coordinates, or null if no visible layers with content are found.
   */
  const extractLayersBoundingBox = useCallback(async (workspaceId: string, layersToUse?: Layer[]): Promise<{ minX: number, minY: number, maxX: number, maxY: number } | null> => {
    if (!kernel.current) return null;
    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) return null;

    let layersToProcess: Layer[];

    if (layersToUse) {
      // If a specific list of layers is provided, use it directly.
      layersToProcess = layersToUse;
    } else {
      // Otherwise, determine the layers in the current view context (active workspace + all its parents).
      layersToProcess = kernel.current.getLayersInWorkspaceContext(workspaceId);
    }

    let minX = Number.MAX_SAFE_INTEGER;
    let minY = Number.MAX_SAFE_INTEGER;
    let maxX = Number.MIN_SAFE_INTEGER;
    let maxY = Number.MIN_SAFE_INTEGER;
    let hasVisibleLayer = false;

    for (const layer of layersToProcess) {
      if (layer.visible) {
        if (layer.imageData) {
          const worldSize = resolveRasterLayerWorldSize(layer);
          if (!worldSize) continue;
          hasVisibleLayer = true;
          const scaledLayerWidth = worldSize.w;
          const scaledLayerHeight = worldSize.h;

          const layerWorldRect = {
            x: workspace.frameRect.x + layer.rect.x,
            y: workspace.frameRect.y + layer.rect.y,
            w: scaledLayerWidth,
            h: scaledLayerHeight,
          };
          const transformedBounds = getTransformedRectBounds(
            layerWorldRect,
            resolveStudioLayerTransform(layer.params),
          );

          minX = Math.min(minX, transformedBounds.x);
          minY = Math.min(minY, transformedBounds.y);
          maxX = Math.max(maxX, transformedBounds.x + transformedBounds.w);
          maxY = Math.max(maxY, transformedBounds.y + transformedBounds.h);
        }
      }
    }

    if (!hasVisibleLayer) {
      // This is not an error, it's a valid state. Return null.
      return null;
    }

    return { minX, minY, maxX, maxY };
  }, [kernel]);



  const zoomToFitAllLayers = useCallback(async (workspaceId: string, layersToFit?: Layer[]): Promise<boolean> => {
    const layersBBox = await extractLayersBoundingBox(workspaceId, layersToFit);
    if (!layersBBox) {
      console.warn("No visible layers found to fit.");
      return false; // No change occurred
    }

    const { minX, minY, maxX, maxY } = layersBBox;
    const rectToFit: Rect = {
      x: minX,
      y: minY,
      w: maxX - minX,
      h: maxY - minY,
    };

    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useCanvas');
    if (!projectId) {
      console.warn(`zoomToFitAllLayers: Could not resolve projectId for workspace "${workspaceId}".`);
      return false;
    }
    return zoomToFitRect(projectId, rectToFit, 0.85);
  }, [extractLayersBoundingBox, kernel, zoomToFitRect]);



  const getIdealLongestCanvasSize = useCallback(async (workspaceId: string, layerFilter?: (layer: Layer) => boolean): Promise<number | undefined> => {
    if (!kernel.current) return undefined;
    const activeWorkspace = kernel.current.getWorkspace(workspaceId);

    if (!activeWorkspace) return undefined;

    const generationRect = activeWorkspace.generationRect;

    let layersToProcess = kernel.current.getLayersInWorkspaceContext(workspaceId);

    if (layerFilter) {
      const matchedLayers = layersToProcess.filter(layerFilter);
      if (matchedLayers.length > 0) {
        layersToProcess = matchedLayers;
      }
    }

    let maxRelativePixelDensity: number = 1;
    const canvasWidth = generationRect.w;
    const canvasHeight = generationRect.h;

    for (const layer of layersToProcess) {
      if (layer.skipRender === true || !layer.visible) {
        continue;
      }

      let intersectsCanvas = false;
      let relativePixelDensity = 1;
      
      const canvasWorldMinX = generationRect.x;
      const canvasWorldMinY = generationRect.y;
      const canvasWorldMaxX = generationRect.x + canvasWidth;
      const canvasWorldMaxY = generationRect.y + canvasHeight;

      if (layer.imageData) {
        const worldSize = resolveRasterLayerWorldSize(layer);
        if (!worldSize) continue;
        const layerScale = resolveRasterLayerScale(layer);
        const layerWidth = worldSize.w;
        const layerHeight = worldSize.h;
        const layerWorldRect = {
          x: activeWorkspace.frameRect.x + layer.rect.x,
          y: activeWorkspace.frameRect.y + layer.rect.y,
          w: layerWidth,
          h: layerHeight,
        };
        const transformedBounds = getTransformedRectBounds(
          layerWorldRect,
          resolveStudioLayerTransform(layer.params),
        );
        const layerMinX = transformedBounds.x;
        const layerMinY = transformedBounds.y;
        const layerMaxX = transformedBounds.x + transformedBounds.w;
        const layerMaxY = transformedBounds.y + transformedBounds.h;

        intersectsCanvas = !(
          layerMaxX < canvasWorldMinX ||
          layerMinX > canvasWorldMaxX ||
          layerMaxY < canvasWorldMinY ||
          layerMinY > canvasWorldMaxY
        );

        if (intersectsCanvas && layerScale !== 0) {
          relativePixelDensity = (1 / layerScale);
        }
      }

      if (intersectsCanvas) {
        maxRelativePixelDensity = Math.max(maxRelativePixelDensity, relativePixelDensity);
      }
    }

    let idealLongestSize: number | undefined = undefined;

    if (canvasWidth > 0 && canvasHeight > 0) {
      const currentCanvasWidth = canvasWidth;
      const currentCanvasHeight = canvasHeight;

      let scaledWidth = currentCanvasWidth * maxRelativePixelDensity;
      let scaledHeight = currentCanvasHeight * maxRelativePixelDensity;

      let longestSide = Math.max(scaledWidth, scaledHeight);

      if (longestSide > 16383) {
        longestSide = 16383;
        if (scaledWidth > scaledHeight) {
          scaledHeight = (16383 / scaledWidth) * scaledHeight;
          scaledWidth = 16383;
        } else {
          scaledWidth = (16383 / scaledHeight) * scaledWidth;
          scaledHeight = 16383;
        }
      }
      idealLongestSize = Math.ceil(longestSide);
    }

    return idealLongestSize;
  }, [kernel]);


  const renderWorkspaceArea = useCallback(async (
    workspaceId: string,
    layerIdsToGrab?: string[],
    targetPixels?: number,
    destroyFillAreaBehindMask: boolean = false,
    alphaChannel: boolean = true,
    multipleOf: number = 1,
    rect: Rect | undefined = undefined,
    outputFormat: OutputImageFormat = 'webp',
    gridOverlay?: GridOverlayOptions,
    maskInterpretation?: MaskInterpretation,
    extraLayers?: Layer[],
    outputQuality?: number,
    background?: string,
    ): Promise<OriginalImage> => {
    const perfStart = performance.now();
    const roundMs = (value: number) => Number(value.toFixed(2));

    if (!kernel.current) {
      throw new Error('gACF: kernel is not available.');
    }
    const shouldLogRenderPerf = kernel.current.getUserPreferences()?.performanceHudEnabled === true;
    const refWorkspace = kernel.current.getWorkspace(workspaceId);

    if (!refWorkspace) {
      throw new Error(`gACF: Reference workspace with ID ${workspaceId} not found.`);
    }

    const generationRect = refWorkspace.generationRect;

    const layers = mergeLayersForRender(kernel.current.getLayersInWorkspaceContext(workspaceId), extraLayers);

    // 1. Determine Initial Dimensions
    const initialWidth = rect ? rect.w : generationRect.w;
    const initialHeight = rect ? rect.h : generationRect.h;

    if (initialWidth <= 0 || initialHeight <= 0) {
      // Throw error instead of console.error + return null
      throw new Error(`gACF: Initial dimensions must be positive. Got: ${initialWidth}x${initialHeight}`);
    }

    let targetWidth: number;
    let targetHeight: number;
    let upscaleFactor: number = 1;

    // 2. Decide Calculation Path: Use new logic ONLY if both constraints are active
    const useAdvancedCalculation = targetPixels && targetPixels > 0;

    if (useAdvancedCalculation) {
        const bestDimensions = findBestDimensions({
            startWidth: initialWidth,
            startHeight: initialHeight,
            targetPixels: targetPixels,
            multipleOf: multipleOf,
            ratioTolerance: 0.002
        });

        if (bestDimensions.width === 0 || bestDimensions.height === 0) {
             // Throw error instead of console.error + return null
            throw new Error(`gACF: findBestDimensions failed to find valid dimensions for initial=${initialWidth}x${initialHeight}, targetPixels=${targetPixels}, multipleOf=${multipleOf}`);
        }

        targetWidth = bestDimensions.width;
        targetHeight = bestDimensions.height;

        // Calculate Upscale Factor based on initial and final dimensions
        const originalLongestSide = Math.max(initialWidth, initialHeight);
        const newLongestSide = Math.max(targetWidth, targetHeight);

        if (originalLongestSide > 0 && newLongestSide > 0) {
            upscaleFactor = newLongestSide / originalLongestSide;
        } else {
            // This case should technically be prevented by earlier checks, but good to be defensive
            upscaleFactor = 1;
        }

    } else {
        targetWidth = initialWidth;
        targetHeight = initialHeight;
        upscaleFactor = 1;

        const idealLongestSize = await getIdealLongestCanvasSize(workspaceId);
        if (idealLongestSize && idealLongestSize > 0) {
            const currentLongestSide = Math.max(targetWidth, targetHeight);
            if (currentLongestSide > 0 && currentLongestSide !== idealLongestSize) {
                const sizeScaleFactor = idealLongestSize / currentLongestSide;
                targetWidth = Math.round(targetWidth * sizeScaleFactor);
                targetHeight = Math.round(targetHeight * sizeScaleFactor);
                upscaleFactor = sizeScaleFactor;
                console.log(`Fallback scaled to idealLongestSize ${idealLongestSize}: New dims approx ${targetWidth}x${targetHeight}`);
            }
        }

        if (multipleOf && multipleOf > 1) {
             const widthBeforeRounding = targetWidth;
             const heightBeforeRounding = targetHeight;

             targetWidth = roundDownToMultipleOf(targetWidth, multipleOf);
             targetHeight = roundDownToMultipleOf(targetHeight, multipleOf);

             if (targetWidth === 0 || targetHeight === 0) {
                 throw new Error(`gACF: Dimensions became zero after simple multipleOf rounding. Initial: ${initialWidth}x${initialHeight}, Scaled: ${widthBeforeRounding}x${heightBeforeRounding}, multipleOf: ${multipleOf}`);
             }

             const originalLongestSide = Math.max(initialWidth, initialHeight);
             const newLongestSide = Math.max(targetWidth, targetHeight);

             if (originalLongestSide > 0 && newLongestSide > 0) {
                 upscaleFactor = newLongestSide / originalLongestSide;
             } else {
                 upscaleFactor = 1;
             }
        }
    }

    // 5. Final Sanity Check
    if (targetWidth <= 0 || targetHeight <= 0) {
        throw new Error(`gACF: Calculated invalid final target dimensions: ${targetWidth}x${targetHeight}. Aborting.`);
    }
    const dimensionCalcEnd = performance.now();

    // 5. Create Temporary Canvas with Adjusted Dimensions
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = targetWidth;
    tempCanvas.height = targetHeight;
    const tempContext = tempCanvas.getContext('2d', { alpha: alphaChannel });
    if (!tempContext) {
      console.warn("tempContext is null");
      return { file: null, upscaleFactor: 1, width: 0, height: 0, targetWidth: 0, targetHeight : 0 }; // Return default upscaleFactor in error case
    }
    if (background) {
      tempContext.fillStyle = background;
      tempContext.fillRect(0, 0, targetWidth, targetHeight);
    }

    let layersToProcess = layers;

    if (layerIdsToGrab !== undefined) {
      if (layerIdsToGrab.length === 0) {
        throw new Error("Layer IDs array cannot be empty.");
      }
      const layerIdSet = new Set(layerIdsToGrab);
      layersToProcess = layers.filter(layer => layerIdSet.has(layer.id));
    }

    const areaToRender = rect || generationRect;
    const timelineCursorMs = Math.max(0, toFiniteTimelineMs(refWorkspace.timelineCursorMs) ?? 0);
    const timelineDurationMs = toFiniteTimelineMs(refWorkspace.timelineDurationMs);
    const timelineEnabledForStillRender =
      refWorkspace.preferVersionPanelOverTimeline !== true &&
      layersToProcess.some((layer) => {
        if (!layer || layer.skipRender === true || layer.visible === false) return false;
        if (layer.type === 'audio') return typeof layer.videoSrc === 'string' && layer.videoSrc.trim().length > 0;
        if (layer.type === 'video') return typeof layer.videoSrc === 'string' && layer.videoSrc.trim().length > 0;
        return Boolean(layer.imageData) || typeof layer.imageSrc === 'string' || layer.type === 'svg' || Boolean(getDrawingLayerPayload(layer.params));
      });
    const resolveLayerTimelineWindow = (layer: Layer) => resolveStillRenderTimelineWindow(layer, timelineDurationMs);
    const activeTopVideoLayerIdAtCursor = timelineEnabledForStillRender
      ? layersToProcess.reduce<{ layerId: string | null; renderOrder: number }>((best, layer, index) => {
          if (layer.skipRender === true || layer.visible === false || layer.type !== 'video' || typeof layer.videoSrc !== 'string' || layer.videoSrc.trim().length === 0) {
            return best;
          }
          const { startMs, endMs } = resolveLayerTimelineWindow(layer);
          if (timelineCursorMs < startMs || timelineCursorMs >= endMs) return best;
          const baseOrder = typeof layer.renderOrder === 'number' && Number.isFinite(layer.renderOrder)
            ? layer.renderOrder
            : index;
          const renderOrder = (layer['top-layer'] === true || Reflect.get(layer, 'topLayer') === true)
            ? baseOrder + 1_000_000
            : baseOrder;
          if (
            best.layerId === null ||
            renderOrder > best.renderOrder ||
            (renderOrder === best.renderOrder && layer.id.localeCompare(best.layerId) > 0)
          ) {
            return { layerId: layer.id, renderOrder };
          }
          return best;
        }, { layerId: null, renderOrder: Number.NEGATIVE_INFINITY }).layerId
      : null;

    const shouldCheckVisibility = layerIdsToGrab === undefined;
    const consideredLayerCount = layersToProcess.length;
    const visibleLayerCount = shouldCheckVisibility
      ? layersToProcess.reduce((count, layer) => count + (layer.visible ? 1 : 0), 0)
      : layersToProcess.length;
    let culledLayerCount = 0;
    let drawnLayerCount = 0;
    const drawLayersStart = performance.now();

    for (const layer of layersToProcess) {
      if (layer.skipRender === true) {
        culledLayerCount += 1;
        continue;
      }
      const isLayerVisible = !shouldCheckVisibility || layer.visible;

      if (isLayerVisible) {
        if (timelineEnabledForStillRender) {
          const { startMs, endMs } = resolveLayerTimelineWindow(layer);
          if (timelineCursorMs < startMs || timelineCursorMs >= endMs) {
            culledLayerCount += 1;
            continue;
          }
          if (
            layer.type === 'video' &&
            activeTopVideoLayerIdAtCursor !== null &&
            layer.id !== activeTopVideoLayerIdAtCursor
          ) {
            culledLayerCount += 1;
            continue;
          }
        }
        if (layer.type === 'video' && layer.videoSrc) {
            let videoElement = kernel.current.videoRefs.current[layer.id];
            let temporaryVideoElement: HTMLVideoElement | null = null;
            try {
              if (!videoElement) {
                temporaryVideoElement = createTemporaryStillRenderVideo(layer.videoSrc);
                videoElement = temporaryVideoElement;
              }
              const { startMs } = resolveLayerTimelineWindow(layer);
              const sourceInMs = Math.max(0, toFiniteTimelineMs(layer.timelineSourceInMs) ?? 0);
              const targetTimeSec = timelineEnabledForStillRender
                ? Math.max(0, (timelineCursorMs - startMs + sourceInMs) / 1000)
                : videoElement.currentTime;
              await seekVideoElementForStillRender(videoElement, targetTimeSec);

              if (videoElement && videoElement.readyState >= 2 && !videoElement.seeking) { // HAVE_CURRENT_DATA or more
                const drawWidth = (layer.rect.w > 0 ? layer.rect.w : videoElement.videoWidth * (layer.scale ?? 1));
                const drawHeight = (layer.rect.h > 0 ? layer.rect.h : videoElement.videoHeight * (layer.scale ?? 1));
                const drawX = refWorkspace.frameRect.x + layer.rect.x;
                const drawY = refWorkspace.frameRect.y + layer.rect.y;
                const layerTransform = resolveStudioLayerTransform(layer.params);
                const transformedBounds = getTransformedRectBounds(
                  { x: drawX, y: drawY, w: drawWidth, h: drawHeight },
                  layerTransform
                );
                const intersectsArea = !(
                  transformedBounds.x + transformedBounds.w <= areaToRender.x ||
                  transformedBounds.y + transformedBounds.h <= areaToRender.y ||
                  transformedBounds.x >= areaToRender.x + areaToRender.w ||
                  transformedBounds.y >= areaToRender.y + areaToRender.h
                );
                if (!intersectsArea) {
                  culledLayerCount += 1;
                  continue;
                }

                drawImageWithStudioLayerTransform(
                    tempContext,
                    videoElement,
                    (drawX - (rect ? rect.x : generationRect.x)) * upscaleFactor,
                    (drawY - (rect ? rect.y : generationRect.y)) * upscaleFactor,
                    drawWidth * upscaleFactor,
                    drawHeight * upscaleFactor,
                    layerTransform
                );
                drawnLayerCount += 1;
              }
            } catch (error) {
              console.warn(`[renderWorkspaceArea] Failed to draw video layer "${layer.id}" for still render.`, error);
            } finally {
              if (temporaryVideoElement) {
                destroyTemporaryStillRenderVideo(temporaryVideoElement);
              }
            }
        } else if (layer.type === 'svg' || getDrawingLayerPayload(layer.params)) {
          const svgSource = getDrawingAwareSvgLayerSource(layer);
          if (!svgSource) {
            continue;
          }
          try {
            const svgImage = await loadSvgImageElement(svgSource);
            const drawWidth = layer.rect.w > 0 ? layer.rect.w : Math.max(1, svgImage.naturalWidth) * (layer.scale ?? 1);
            const drawHeight = layer.rect.h > 0 ? layer.rect.h : Math.max(1, svgImage.naturalHeight) * (layer.scale ?? 1);
            const drawX = refWorkspace.frameRect.x + layer.rect.x;
            const drawY = refWorkspace.frameRect.y + layer.rect.y;
            const layerTransform = resolveStudioLayerTransform(layer.params);
            const transformedBounds = getTransformedRectBounds(
              { x: drawX, y: drawY, w: drawWidth, h: drawHeight },
              layerTransform
            );
            const intersectsArea = !(
              transformedBounds.x + transformedBounds.w <= areaToRender.x ||
              transformedBounds.y + transformedBounds.h <= areaToRender.y ||
              transformedBounds.x >= areaToRender.x + areaToRender.w ||
              transformedBounds.y >= areaToRender.y + areaToRender.h
            );
            if (!intersectsArea) {
              culledLayerCount += 1;
              continue;
            }
            drawImageWithStudioLayerTransform(
              tempContext,
              svgImage,
              (drawX - (rect ? rect.x : generationRect.x)) * upscaleFactor,
              (drawY - (rect ? rect.y : generationRect.y)) * upscaleFactor,
              drawWidth * upscaleFactor,
              drawHeight * upscaleFactor,
              layerTransform
            );
            drawnLayerCount += 1;
          } catch (error) {
            console.error(`[renderWorkspaceArea] Failed to draw SVG layer "${layer.id}".`, error);
          }
        } else {
          if (layer.imageData) {
            const tempLayerCanvas = new OffscreenCanvas(layer.imageData.width, layer.imageData.height);
            const tempLayerCtx = tempLayerCanvas.getContext('2d');
           if (tempLayerCtx) {
              tempLayerCtx.putImageData(layer.imageData, 0, 0);

              const worldSize = resolveRasterLayerWorldSize(layer);
              if (!worldSize) continue;
              const drawWidth = worldSize.w;
              const drawHeight = worldSize.h;
              const drawX = refWorkspace.frameRect.x + layer.rect.x;
              const drawY = refWorkspace.frameRect.y + layer.rect.y;
              const drawWorldWidth = drawWidth;
              const drawWorldHeight = drawHeight;
              const layerTransform = resolveStudioLayerTransform(layer.params);
              const transformedBounds = getTransformedRectBounds(
                { x: drawX, y: drawY, w: drawWorldWidth, h: drawWorldHeight },
                layerTransform
              );
              const intersectsArea = !(
                transformedBounds.x + transformedBounds.w <= areaToRender.x ||
                transformedBounds.y + transformedBounds.h <= areaToRender.y ||
                transformedBounds.x >= areaToRender.x + areaToRender.w ||
                transformedBounds.y >= areaToRender.y + areaToRender.h
              );
              if (!intersectsArea) {
                culledLayerCount += 1;
                continue;
              }

              // 6. Draw Layers with Final Upscale Factor
              drawImageWithStudioLayerTransform(
                tempContext,
                tempLayerCanvas,
                (drawX - (rect ? rect.x : generationRect.x)) * upscaleFactor,
                (drawY - (rect ? rect.y : generationRect.y)) * upscaleFactor,
                drawWidth * upscaleFactor,
                drawHeight * upscaleFactor,
                layerTransform
              );
              drawnLayerCount += 1;
            }
          }
        }
      }
    }
    const drawLayersEnd = performance.now();


    const overlaysStart = performance.now();
    if (maskInterpretation === 'drawAnnotationOverlay') {
      const mask = resolveWorkspaceAnnotationMask(refWorkspace);
      if (mask) {
        const overlayCanvas = document.createElement('canvas');
        overlayCanvas.width = mask.width;
        overlayCanvas.height = mask.height;
        const overlayCtx = overlayCanvas.getContext('2d');

        if (overlayCtx) {
          // Put the raw annotation data onto a temp canvas
          overlayCtx.putImageData(mask, 0, 0);

          const scaleX = mask.width / refWorkspace.frameRect.w;
          const scaleY = mask.height / refWorkspace.frameRect.h;

          const sx = (areaToRender.x - refWorkspace.frameRect.x) * scaleX;
          const sy = (areaToRender.y - refWorkspace.frameRect.y) * scaleY;
          const sWidth = areaToRender.w * scaleX;
          const sHeight = areaToRender.h * scaleY;

          // Draw the annotation canvas onto the main canvas, scaling it to fit the output dimensions.
          // Keep source pixel alpha intact; the workspace flag controls only the overlay opacity.
          drawWorkspaceAnnotationOverlay(
            tempContext,
            overlayCanvas,
            sx,
            sy,
            sWidth,
            sHeight,
            tempCanvas.width,
            tempCanvas.height,
            refWorkspace.annotationOpaque,
          );
        }
      }
    }


    // --- Draw Grid Overlay ---
    if (gridOverlay) {
      tempContext.strokeStyle = gridOverlay.lineColor;
      tempContext.lineWidth = gridOverlay.lineWidth;

      const cellWidth = tempCanvas.width / gridOverlay.subdivisionsX;
      const cellHeight = tempCanvas.height / gridOverlay.subdivisionsY;

      // Draw vertical lines
      for (let i = 1; i < gridOverlay.subdivisionsX; i++) {
        const x = i * cellWidth;
        tempContext.beginPath();
        tempContext.moveTo(x, 0);
        tempContext.lineTo(x, tempCanvas.height);
        tempContext.stroke();
      }

      // Draw horizontal lines
      for (let i = 1; i < gridOverlay.subdivisionsY; i++) {
        const y = i * cellHeight;
        tempContext.beginPath();
        tempContext.moveTo(0, y);
        tempContext.lineTo(tempCanvas.width, y);
        tempContext.stroke();
      }
    }
    const overlaysEnd = performance.now();

    const imageDataReadStart = performance.now();
    const imageData = tempContext.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    const imageDataReadEnd = performance.now();

    return new Promise<OriginalImage>((resolve) => {
      let mimeType: string;
      let fileExtension: string;
      let quality: number | undefined = undefined; // Quality setting for lossy formats
      const normalizedQuality =
        typeof outputQuality === 'number' && Number.isFinite(outputQuality)
          ? Math.max(0.05, Math.min(1, outputQuality))
          : 1.0;

      switch (outputFormat) {
          case 'png':
              mimeType = 'image/png';
              fileExtension = 'png';
              break;
          case 'jpeg':
              mimeType = 'image/jpeg';
              fileExtension = 'jpeg';
              quality = normalizedQuality;
              break;
          case 'webp':
          default: // Default to webp
              mimeType = 'image/webp';
              fileExtension = 'webp';
              quality = normalizedQuality;
              if (outputFormat !== 'webp') {
                   console.warn(`gACF: Invalid outputFormat "${outputFormat}" provided. Defaulting to 'webp'.`);
              }
              break;
      }

      const fileName = `background.${fileExtension}`;
      const toBlobStart = performance.now();
      
      tempCanvas.toBlob((blob) => {
        const toBlobEnd = performance.now();
        const perfPayload = shouldLogRenderPerf
          ? {
              workspaceId,
              inputSize: `${initialWidth}x${initialHeight}`,
              outputSize: `${targetWidth}x${targetHeight}`,
              targetPixels: targetPixels ?? null,
              outputFormat,
              maskInterpretation: maskInterpretation ?? null,
              hasGridOverlay: Boolean(gridOverlay),
              consideredLayerCount,
              visibleLayerCount,
              culledLayerCount,
              drawnLayerCount,
              timingsMs: {
                dimensions: roundMs(dimensionCalcEnd - perfStart),
                drawLayers: roundMs(drawLayersEnd - drawLayersStart),
                overlays: roundMs(overlaysEnd - overlaysStart),
                readImageData: roundMs(imageDataReadEnd - imageDataReadStart),
                toBlob: roundMs(toBlobEnd - toBlobStart),
                total: roundMs(toBlobEnd - perfStart),
              },
            }
          : null;

        if (blob) {
          if (perfPayload) {
            console.log('[Perf][renderWorkspaceArea] done', perfPayload);
          }
          // Use determined fileName and mimeType
          const file = new File([blob], fileName, { type: mimeType });
          resolve({
            file,
            upscaleFactor,
            width: rect ? rect.w : generationRect.w,
            height: rect ? rect.h : generationRect.h,
            targetWidth: tempCanvas.width,
            targetHeight: tempCanvas.height,
            imageData,
          });
        } else {
          console.error(`gACF: Failed to create blob with format ${mimeType}`);
          if (perfPayload) {
            console.warn('[Perf][renderWorkspaceArea] failed (toBlob returned null)', perfPayload);
          }
          resolve({ file: null, upscaleFactor: 1, width: 0, height: 0, targetWidth: 0, targetHeight: 0, imageData: null });
        }
      // Pass determined mimeType and optional quality
      }, mimeType, quality);
    });
  }, [kernel, getIdealLongestCanvasSize]);


  const renderImageExport = useCallback(async (options: ImageExportRenderOptions): Promise<OriginalImage> => {
    const {
      workspaceId,
      target,
      format,
      targetPixels,
      exportWidth,
      exportHeight,
      qualityPercent,
      alpha,
      includeAnnotations,
    } = options;
    const noVisibleLayersMessage = options.noVisibleLayersMessage || 'No visible layers found in this workspace.';
    const sourceImageUrl = typeof options.sourceImageUrl === 'string' && options.sourceImageUrl.trim().length > 0
      ? options.sourceImageUrl.trim()
      : null;
    const workspace = workspaceId ? kernel.current?.getWorkspace(workspaceId) ?? null : null;
    if (!kernel.current || (!workspace && !sourceImageUrl)) {
      throw new Error('Workspace not available for export.');
    }

    if (format === 'svg') {
      const layerRegistry = kernel.current.getLayerRegistry();
      const visibleImageExportLayers = workspace
        ? collectVisibleImageExportLayers(workspace.layerIds, layerRegistry)
        : [];
      const nativeSvgExportAvailable = Boolean(workspace) && isNativeSvgExportAvailable({
        isSourceImageMode: Boolean(sourceImageUrl),
        hasTimelineMedia: workspace ? hasVisibleTimelineMedia(workspace.layerIds, layerRegistry) : false,
        includeAnnotations,
        visibleImageExportLayers,
      });
      if (!nativeSvgExportAvailable || !workspace) {
        throw new Error('SVG export is only available when all visible image layers are native SVG and annotations are disabled.');
      }
      const exportRect = target === 'frame' ? workspace.frameRect : workspace.generationRect;
      const fragments = (await Promise.all(
        visibleImageExportLayers.map(async (layer) => {
          const drawingSvg = getDrawingLayerSvgSource(layer.params);
          const inlineSvg = drawingSvg?.svg ?? await resolveSvgLayerInlineMarkup(layer);
          return buildNativeSvgLayerFragment(layer, workspace.frameRect, exportRect, {
            inlineSvg,
            inlineViewBox: drawingSvg?.viewBox,
          });
        })
      )).filter((fragment): fragment is string => typeof fragment === 'string' && fragment.length > 0);
      if (fragments.length === 0) {
        throw new Error(noVisibleLayersMessage);
      }
      const svg = buildNativeSvgDocument({
        width: exportWidth,
        height: exportHeight,
        viewBoxWidth: exportRect.w,
        viewBoxHeight: exportRect.h,
        fragments,
      });
      return {
        file: new File([svg], 'background.svg', { type: 'image/svg+xml' }),
        upscaleFactor: 1,
        width: exportRect.w,
        height: exportRect.h,
        targetWidth: exportWidth,
        targetHeight: exportHeight,
        imageData: null,
      };
    }

    const rasterFormat: OutputImageFormat = format === 'pdf' ? 'png' : format;
    const renderAlpha = format === 'pdf' ? true : alpha;
    const rasterQuality = (rasterFormat === 'jpeg' || rasterFormat === 'webp') ? qualityPercent / 100 : undefined;

    if (options.print && sourceImageUrl) throw new Error('PRINT_UNSUPPORTED_SOURCE');

    // Source-image mode: same settings, the pixels come from the asset.
    if (sourceImageUrl) {
      const rendered = await renderSourceImageExport(sourceImageUrl, targetPixels, rasterFormat, rasterQuality, renderAlpha);
      if (!rendered.file || format !== 'pdf') {
        return rendered;
      }
      const documentFile = await createPdfExportFile(rendered.file, rendered.targetWidth, rendered.targetHeight, qualityPercent / 100);
      return { ...rendered, file: documentFile };
    }
    if (!workspace) {
      throw new Error('Workspace not available for export.');
    }

    if (options.print) {
      if (sourceImageUrl) throw new Error('PRINT_UNSUPPORTED_SOURCE');
      const printRect = options.print.target === 'frame' ? workspace.frameRect : workspace.generationRect;
      const visibleLayers = kernel.current.getLayersInWorkspaceContext(workspace.id).filter((layer) => layer.visible !== false);
      if (visibleLayers.length === 0) throw new Error(noVisibleLayersMessage);
      const icc = await loadPrintIccProfile(options.print.iccProfile);
      const snapshot = buildPrintSnapshot({
        layers: visibleLayers,
        areaRect: printRect,
        frameRect: workspace.frameRect,
        width: options.exportWidth,
        height: options.exportHeight,
        options: options.print,
        icc,
      });
      const rendered = await runPrintExport({ snapshot, signal: options.printSignal, onProgress: options.onPrintProgress });
      return {
        file: rendered.file,
        upscaleFactor: 1,
        width: printRect.w,
        height: printRect.h,
        targetWidth: snapshot.width,
        targetHeight: snapshot.height,
        imageData: null,
      };
    }

    const includeMaskInterpretation: MaskInterpretation | undefined = includeAnnotations
      ? 'drawAnnotationOverlay'
      : undefined;

    let layerIdsToGrab: string[] | undefined = undefined;
    let rectToRender: Rect | undefined = undefined;

    if (target === 'frame') {
      const visibleLayerIds = kernel.current
        .getLayersInWorkspaceContext(workspace.id)
        .filter((layer) => layer.visible)
        .map((layer) => layer.id);

      if (visibleLayerIds.length === 0) {
        throw new Error(noVisibleLayersMessage);
      }

      layerIdsToGrab = visibleLayerIds;
      rectToRender = workspace.frameRect;
    }

    const rendered = await renderWorkspaceArea(
      workspace.id,
      layerIdsToGrab,
      targetPixels,
      false,
      renderAlpha,
      1,
      rectToRender,
      rasterFormat,
      undefined,
      includeMaskInterpretation,
      undefined,
      rasterQuality
    );

    if (!rendered.file || format !== 'pdf') {
      return rendered;
    }

    const documentFile = await createPdfExportFile(rendered.file, rendered.targetWidth, rendered.targetHeight, qualityPercent / 100);

    return {
      ...rendered,
      file: documentFile,
    };
  }, [kernel, renderWorkspaceArea]);

  const renderWorkspaceAreaToNewLayer = useCallback(async (
    projectId: string,
    workspaceId: string
  ): Promise<Layer | null> => {
    if (!kernel.current) return null;
    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) return null;

    const frameRect = workspace.frameRect;
    const canvasRect = workspace.generationRect;

    const frameArea = Math.max(1, frameRect.w * frameRect.h);
    const canvasArea = Math.max(1, canvasRect.w * canvasRect.h);
    const areaRatio = canvasArea / frameArea;

    // If the full frame is rendered, target roughly 4K density (~16.4 Mpx).
    const FRAME_4K_REFERENCE_PIXELS = 16_400_000;
    const MIN_TARGET_PIXELS = 4_096;
    const targetPixels = Math.max(MIN_TARGET_PIXELS, Math.round(FRAME_4K_REFERENCE_PIXELS * areaRatio));

    kernel.current.logUserAction('Render Canvas Area To New Layer Requested', {
      workspaceId: workspace.id,
      canvasRect,
      frameRect,
      areaRatio,
      targetPixels,
    });

    try {
      const rendered = await renderWorkspaceArea(
        workspace.id,
        undefined,
        targetPixels,
        false,
        true,
        1,
        canvasRect,
        'webp'
      );

      if (!rendered.imageData) {
        throw new Error('Render failed: no image data returned.');
      }

      const inferredScale = 1 / (rendered.upscaleFactor || 1);
      const fallbackScale = canvasRect.w > 0 ? (canvasRect.w / rendered.imageData.width) : 1;
      const layerScale = Number.isFinite(inferredScale) && inferredScale > 0 ? inferredScale : fallbackScale;

      const newLayer = kernel.current.addLayerFromImageData(workspace.id, rendered.imageData, {
        name: `Canvas Render ${Date.now()}`,
        rect: {
          x: canvasRect.x - frameRect.x,
          y: canvasRect.y - frameRect.y,
          w: rendered.imageData.width * layerScale,
          h: rendered.imageData.height * layerScale,
        },
        scale: layerScale,
        locked: true,
        source: 'workspace_render_area',
      });

      kernel.current.selectLayer(projectId, newLayer.id, false);
      kernel.current.invalidateWorkspaceVersionCache(workspace.id);
      kernel.current.logUserAction('Render Canvas Area To New Layer Completed', {
        workspaceId: workspace.id,
        newLayerId: newLayer.id,
        targetPixels,
        outputWidth: rendered.imageData.width,
        outputHeight: rendered.imageData.height,
      });
      return newLayer;
    } catch (error) {
      console.error('Render canvas to layer failed:', error);
      kernel.current.handleError((error as Error)?.message || 'Failed to render canvas area to new layer.');
      return null;
    }
  }, [kernel, renderWorkspaceArea]);


  const zoomOnCanvas = useCallback((workspaceId: string): boolean => {
    if (!kernel.current) return false;
    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) {
      console.warn(`zoomOnCanvas: Workspace with ID "${workspaceId}" not found.`);
      return false;
    }
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useCanvas');
    if (!projectId) {
      console.warn(`zoomOnCanvas: Could not resolve projectId for workspace "${workspaceId}".`);
      return false;
    }
    return zoomToFitRect(projectId, workspace.generationRect, 0.85);
  }, [kernel, zoomToFitRect]);


  const calculateFitToVisibleContentLayersRect = useCallback(async (workspaceId: string, marginFactor?: number): Promise<CalculatedFitRect | null> => {
    if (!kernel.current) return null;
    const layersBBox = await extractLayersBoundingBox(workspaceId); // Gets all visible layers in active workspace

    const activeWorkspace = kernel.current.getWorkspace(workspaceId);
    if (!activeWorkspace) {
      console.error("calculateFitToVisibleContentLayersRect: Active workspace not found.");
      return null;
    }

    if (!layersBBox) {
      console.warn("calculateFitToVisibleContentLayersRect: No visible layers found, cannot calculate new area.");
      // Instead of throwing, return null to allow the caller (command handler) to decide how to proceed.
      // For example, it might do nothing or show a toast.
      return null;
    }

    const contentMinX = layersBBox.minX;
    const contentMinY = layersBBox.minY;
    const contentMaxX = layersBBox.maxX;
    const contentMaxY = layersBBox.maxY;

    let newGenerationAreaWidth = Math.max(0.00001, contentMaxX - contentMinX);
    let newGenerationAreaHeight = Math.max(0.00001, contentMaxY - contentMinY);
    let newGenerationAreaX = contentMinX;
    let newGenerationAreaY = contentMinY;

    if (marginFactor && marginFactor > 1.0) {
      const centerX = newGenerationAreaX + newGenerationAreaWidth / 2;
      const centerY = newGenerationAreaY + newGenerationAreaHeight / 2;
      newGenerationAreaWidth *= marginFactor;
      newGenerationAreaHeight *= marginFactor;
      newGenerationAreaX = centerX - newGenerationAreaWidth / 2;
      newGenerationAreaY = centerY - newGenerationAreaHeight / 2;
    }

    const aspectRatio = activeWorkspace.aspectRatio;
    if (aspectRatio) {
      const currentRatio = newGenerationAreaWidth / newGenerationAreaHeight;
      const centerX = newGenerationAreaX + newGenerationAreaWidth / 2;
      const centerY = newGenerationAreaY + newGenerationAreaHeight / 2;

      if (currentRatio > aspectRatio) {
        newGenerationAreaHeight = newGenerationAreaWidth / aspectRatio;
      } else {
        newGenerationAreaWidth = newGenerationAreaHeight * aspectRatio;
      }
      newGenerationAreaX = centerX - newGenerationAreaWidth / 2;
      newGenerationAreaY = centerY - newGenerationAreaHeight / 2;
    }

    if (newGenerationAreaWidth <= 0 || newGenerationAreaHeight <= 0) {
      console.warn("calculateFitToVisibleContentLayersRect: Invalid bounding box dimensions after calculations.");
      return null;
    }
    
    const generationRect = activeWorkspace.generationRect;
    const oldGenerationAreaX = generationRect.x;
    const oldGenerationAreaY = generationRect.y;
    const gaShiftDx = newGenerationAreaX - oldGenerationAreaX;
    const gaShiftDy = newGenerationAreaY - oldGenerationAreaY;

    const frameRect = activeWorkspace.frameRect;

    // Final clamp to ensure the result is within the active frameRect
    const finalX = Math.max(frameRect.x, newGenerationAreaX);
    const finalY = Math.max(frameRect.y, newGenerationAreaY);
    const finalW = Math.min(newGenerationAreaWidth, (frameRect.x + frameRect.w) - finalX);
    const finalH = Math.min(newGenerationAreaHeight, (frameRect.y + frameRect.h) - finalY);

    return {
      x: finalX,
      y: finalY,
      w: finalW,
      h: finalH,
      shiftDx: gaShiftDx,
      shiftDy: gaShiftDy,
    };
  }, [ extractLayersBoundingBox, kernel ]);


  const fitCanvasToVisibleContentLayers = useCallback(async (workspaceId: string, marginFactor?: number) => {
    if (!kernel.current) return;
    const calculatedRect = await calculateFitToVisibleContentLayersRect(workspaceId, marginFactor);

    if (!calculatedRect) {
      return; 
    }

    const { x, y, w, h, shiftDx, shiftDy } = calculatedRect;

    const activeWorkspace = kernel.current.getWorkspace(workspaceId);
    if (!activeWorkspace) {
      throw new Error("setGenerationArea: Active workspace not found.");
    }
    const generationRect = activeWorkspace.generationRect;
    const isSameBoundingBox = (
      Math.abs(w - generationRect.w) < 0.01 &&
      Math.abs(h - generationRect.h) < 0.01 &&
      Math.abs(x - generationRect.x) < 0.01 &&
      Math.abs(y - generationRect.y) < 0.01
    );

    if (isSameBoundingBox) {
      await zoomToFitAllLayers(workspaceId); 
      return;
    }

    kernel.current?.setGenerationArea(workspaceId, x, y, w, h);
  }, [calculateFitToVisibleContentLayersRect, zoomToFitAllLayers, kernel]);


  const setGenerationArea = useCallback(async (
    workspaceId: string,
    newX: number,
    newY: number,
    newWidth: number,
    newHeight: number,
    _suppressSaveState: boolean = false
    ) => {
    if (!kernel.current) {
      throw new Error("setGenerationArea: kernel is not available.");
    }
    if (newWidth <= 0 || newHeight <= 0) {
      console.warn("setGenerationArea: width and height must be positive.");
      throw new Error("Generation Area width and height must be positive.");
    }
    
    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) {
      throw new Error("setGenerationArea: Active workspace not found.");
    }
    const workspaceRow = kernel.current.getInventory().StudioWorkspace.registry[workspaceId];
    const workspaceProjectId =
      typeof workspaceRow?.projectId === 'string' ? workspaceRow.projectId.trim() : '';
    if (!workspaceProjectId) {
      throw new Error(`setGenerationArea: Missing explicit project scope for workspace "${workspaceId}".`);
    }

    const frameRect = workspace.frameRect;

    let finalX: number, finalY: number, finalWidth: number, finalHeight: number;
    let aspectRatioCorrection: { ratio: number } | null = null;

    // --- EXCEPTION: If the selection covers the entire frame, match the frame and ignore aspect ratio ---
    const coversFrame =
        newX <= frameRect.x &&
        newY <= frameRect.y &&
        (newX + newWidth) >= (frameRect.x + frameRect.w) &&
        (newY + newHeight) >= (frameRect.y + frameRect.h);

    if (coversFrame) {
        finalX = frameRect.x;
        finalY = frameRect.y;
        finalWidth = frameRect.w;
        finalHeight = frameRect.h;
    } else {
        // --- Standard logic for all other cases ---

        // 1. Calculate the intersection of the user's proposed rect and the frame.
        // This represents the valid part of the user's selection.
        const intersectionX1 = Math.max(newX, frameRect.x);
        const intersectionY1 = Math.max(newY, frameRect.y);
        const intersectionX2 = Math.min(newX + newWidth, frameRect.x + frameRect.w);
        const intersectionY2 = Math.min(newY + newHeight, frameRect.y + frameRect.h);

        const intersectionWidth = Math.max(0, intersectionX2 - intersectionX1);
        const intersectionHeight = Math.max(0, intersectionY2 - intersectionY1);

        if (intersectionWidth <= 0 || intersectionHeight <= 0) {
            // User dragged completely outside the frame or created a zero-area rect.
            // We cannot proceed as it would lead to an invalid state.
            return;
        }

        // --- Smart Constraint Enforcement ---
        if (typeof workspace.aspectRatio === 'number' && workspace.aspectRatio > 0) {
            const aspectRatio = workspace.aspectRatio;

            // 2. Calculate the area of the intersection. This is the target area for our new rect.
            const intersectionArea = intersectionWidth * intersectionHeight;

            // 3. Calculate dimensions for a new rect with the correct aspect ratio and target area.
            let targetHeight = Math.sqrt(intersectionArea / aspectRatio);
            let targetWidth = targetHeight * aspectRatio;

            // 4. The new rect might be larger than the frame. Scale it down to fit, preserving the aspect ratio.
            const scaleFactor = Math.min(1.0, frameRect.w / targetWidth, frameRect.h / targetHeight);

            finalWidth = targetWidth * scaleFactor;
            finalHeight = targetHeight * scaleFactor;

            // 5. Position the final rect based on whether the intersection touches the frame's boundaries.
            const touchesLeft = Math.abs(intersectionX1 - frameRect.x) < 1;
            const touchesTop = Math.abs(intersectionY1 - frameRect.y) < 1;
            const touchesRight = Math.abs(intersectionX2 - (frameRect.x + frameRect.w)) < 1;
            const touchesBottom = Math.abs(intersectionY2 - (frameRect.y + frameRect.h)) < 1;

            const intersectionCenterX = intersectionX1 + intersectionWidth / 2;
            const intersectionCenterY = intersectionY1 + intersectionHeight / 2;

            let idealX: number;
            let idealY: number;

            // Determine X position: Stick to edges, or center if contained or spanning the whole frame.
            if (touchesLeft && !touchesRight) {
                idealX = frameRect.x; // Stick to left
            } else if (touchesRight && !touchesLeft) {
                idealX = frameRect.x + frameRect.w - finalWidth; // Stick to right
            } else {
                // Contained within frame, or spanning the entire frame width. Center it.
                idealX = intersectionCenterX - finalWidth / 2;
            }

            // Determine Y position: Stick to edges, or center if contained or spanning the whole frame.
            if (touchesTop && !touchesBottom) {
                idealY = frameRect.y; // Stick to top
            } else if (touchesBottom && !touchesTop) {
                idealY = frameRect.y + frameRect.h - finalHeight; // Stick to bottom
            } else {
                // Contained within frame, or spanning the entire frame height. Center it.
                idealY = intersectionCenterY - finalHeight / 2;
            }

            // Final clamp to ensure the entire rectangle is within the frame.
            finalX = Math.max(frameRect.x, Math.min(idealX, frameRect.x + frameRect.w - finalWidth));
            finalY = Math.max(frameRect.y, Math.min(idealY, frameRect.y + frameRect.h - finalHeight));

            const correctionDelta =
              Math.abs(finalX - intersectionX1) +
              Math.abs(finalY - intersectionY1) +
              Math.abs(finalWidth - intersectionWidth) +
              Math.abs(finalHeight - intersectionHeight);
            if (correctionDelta > 0.5) {
              aspectRatioCorrection = { ratio: aspectRatio };
            }

        } else {
            // No aspect ratio constraint, so the result is simply the intersection.
            finalX = intersectionX1;
            finalY = intersectionY1;
            finalWidth = intersectionWidth;
            finalHeight = intersectionHeight;
        }
    }

    kernel.current.setWorkspace(workspaceId, (workspace) => ({
      ...workspace,
      generationRect: { x: finalX, y: finalY, w: finalWidth, h: finalHeight },
    }), workspaceProjectId);

    if (aspectRatioCorrection) {
      const previousPulseToken = kernel.current.readProjectRuntimeState(workspaceProjectId)
        .aspectRatioEnforcementPulse?.token;
      kernel.current.patchProjectRuntimeState(workspaceProjectId, {
        aspectRatioEnforcementPulse: {
          workspaceId,
          ratio: aspectRatioCorrection.ratio,
          token: (typeof previousPulseToken === 'number' ? previousPulseToken : 0) + 1,
        },
      });
    }
  }, [kernel]);



  const renderWorkspaceView = useCallback(async (
    workspaceId: string,
    targetPixels?: number, // Optional: target total pixels for the output image
    outputFormat: OutputImageFormat = 'webp' // Default to webp
  ): Promise<OriginalImage | null> => {
    if (!kernel.current) return null;
    const contentLayers = kernel.current.getLayersInWorkspaceContext(workspaceId);

    if (contentLayers.length === 0) {
      console.warn("renderWorkspaceView: No visible non-control content layers found.");
      return null; // Or return a default empty OriginalImage if preferred
    }

    // 2. Determine the bounding box of these content layers (in world coordinates)
    // We can reuse extractLayersBoundingBox by passing it our filtered contentLayers
    const contentBoundingBox = await extractLayersBoundingBox(workspaceId, contentLayers);

    if (!contentBoundingBox) {
      console.warn("renderWorkspaceView: Could not determine bounding box for content layers.");
      return null;
    }

    const { minX, minY, maxX, maxY } = contentBoundingBox;
    const worldRect: Rect = {
      x: minX,
      y: minY,
      w: Math.max(0.001, maxX - minX), // Ensure width is positive
      h: Math.max(0.001, maxY - minY), // Ensure height is positive
    };

    // 3. Get the IDs of these layers
    const layerIdsToGrab = contentLayers.map(layer => layer.id);

    // 4. Call renderWorkspaceArea to render these layers within their collective bounding box
    try {
      const assembledImage = await renderWorkspaceArea(
        workspaceId,
        layerIdsToGrab,
        targetPixels, // Pass through targetPixels
        false,        // destroyFillAreaBehindMask (not relevant here)
        true,         // alphaChannel (true is good for context images)
        1,            // multipleOf
        worldRect,    // rect: Use the calculated bounding box of all visible content
        outputFormat  // Pass through outputFormat
      );
      return assembledImage;
    } catch (error) {
      console.error("renderWorkspaceView: Error calling renderWorkspaceArea:", error);
      return null;
    }
  }, [extractLayersBoundingBox, renderWorkspaceArea, kernel]);


  const calculateAdjustGenerationAreaRelativeRect = useCallback(async (
    workspaceId: string,
    referenceAreaType: 'currentGA' | 'visibleContent',
    reductionPercentage: number,
    anchorPoint: string // e.g., "topLeft", "center", "bottomRight"
  ): Promise<CalculatedFitRect | null> => {
    if (!kernel.current) return null;
    let baseRect: Rect;
    
    const activeWorkspace = kernel.current.getWorkspace(workspaceId);
    if (!activeWorkspace) {
      throw new Error("setGenerationArea: Active workspace not found.");
    }
    const generationRect = activeWorkspace.generationRect;

    if (referenceAreaType === 'currentGA') {
      baseRect = { ...generationRect }; // Use a copy of the current GA
    } else { // 'visibleContent'
      const visibleContentFitRect = await calculateFitToVisibleContentLayersRect(workspaceId, 1.0); // No margin factor
      if (!visibleContentFitRect) {
        console.warn("ADJUST_GA_RELATIVE: No visible content found to use as reference area.");
        return null;
      }
      baseRect = { x: visibleContentFitRect.x, y: visibleContentFitRect.y, w: visibleContentFitRect.w, h: visibleContentFitRect.h };
    }

    if (baseRect.w <= 0 || baseRect.h <= 0) {
      console.warn("ADJUST_GA_RELATIVE: Base reference area has invalid dimensions.");
      return null;
    }

    // Calculate new dimensions while maintaining aspect ratio of baseRect
    const areaReductionFactor = 1.0 - (reductionPercentage / 100.0);
    const dimensionScalingFactor = Math.sqrt(areaReductionFactor);

    let newW = baseRect.w * dimensionScalingFactor;
    let newH = baseRect.h * dimensionScalingFactor;

    // Ensure minimum dimensions (e.g., 1px, or a more practical minimum like 8px)
    const minDimension = 8;
    newW = Math.max(minDimension, newW);
    newH = Math.max(minDimension, newH);

    // If new dimensions are larger than base (e.g. due to minDimension), cap them at baseRect size
    newW = Math.min(newW, baseRect.w);
    newH = Math.min(newH, baseRect.h);


    let newX: number;
    let newY: number;

    // Calculate newX, newY based on anchorPoint, ensuring it sticks to baseRect borders
    switch (anchorPoint) {
      case "topLeft":
        newX = baseRect.x;
        newY = baseRect.y;
        break;
      case "topCenter":
        newX = baseRect.x + (baseRect.w / 2) - (newW / 2);
        newY = baseRect.y;
        break;
      case "topRight":
        newX = baseRect.x + baseRect.w - newW;
        newY = baseRect.y;
        break;
      case "middleLeft":
        newX = baseRect.x;
        newY = baseRect.y + (baseRect.h / 2) - (newH / 2);
        break;
      case "center":
        newX = baseRect.x + (baseRect.w / 2) - (newW / 2);
        newY = baseRect.y + (baseRect.h / 2) - (newH / 2);
        break;
      case "middleRight":
        newX = baseRect.x + baseRect.w - newW;
        newY = baseRect.y + (baseRect.h / 2) - (newH / 2);
        break;
      case "bottomLeft":
        newX = baseRect.x;
        newY = baseRect.y + baseRect.h - newH;
        break;
      case "bottomCenter":
        newX = baseRect.x + (baseRect.w / 2) - (newW / 2);
        newY = baseRect.y + baseRect.h - newH;
        break;
      case "bottomRight":
        newX = baseRect.x + baseRect.w - newW;
        newY = baseRect.y + baseRect.h - newH;
        break;
      default:
        console.warn(`ADJUST_GA_RELATIVE: Unknown anchorPoint "${anchorPoint}". Defaulting to center.`);
        newX = baseRect.x + (baseRect.w / 2) - (newW / 2);
        newY = baseRect.y + (baseRect.h / 2) - (newH / 2);
    }

    // Calculate shiftDx and shiftDy relative to the *current* generationRect (the current GA)
    // This is what resizeAndRestoreMask will use to understand how the mask's content should be reprojected.
    const shiftDx = newX - generationRect.x;
    const shiftDy = newY - generationRect.y;

    return {
      x: newX,
      y: newY,
      w: newW,
      h: newH,
      shiftDx: shiftDx,
      shiftDy: shiftDy,
    };
  }, [kernel, calculateFitToVisibleContentLayersRect]);


  const zoomOnLayer = useCallback(async (layerOrId: Layer | string) => {
    if (!kernel.current) return;
    let layer: Layer | undefined;

    if (typeof layerOrId === 'string') {
      layer = kernel.current.getLayerRegistry()[layerOrId];
    } else {
      layer = layerOrId;
    }

    if (!layer) {
      console.warn("zoomOnLayer: Layer not found.");
      return;
    }
    if (!layer.layerWorkspaceId) {
      console.warn('zoomOnLayer: Project layers cannot be zoomed in a workspace context.');
      return;
    }

    const projectId = requireWorkspaceProjectIdFromKernel(kernel, layer.layerWorkspaceId, 'useCanvas');
    if (!projectId) {
      console.warn(`zoomOnLayer: Could not resolve projectId for layer workspace "${layer.layerWorkspaceId}".`);
      return;
    }

    if (!layer.imageData) {
      console.warn("zoomOnLayer: Layer not found or no ImageData.");
      return;
    }

    const activeWorkspace = kernel.current.getWorkspace(layer.layerWorkspaceId);
    if (!activeWorkspace) return;

    const layerImageData = layer.imageData;
    const originalWidth = layerImageData.width;
    const originalHeight = layerImageData.height;
    const scale = resolveRasterLayerScale(layer);
    let minLocalX = Infinity;
    let minLocalY = Infinity;
    let maxLocalX = -Infinity;
    let maxLocalY = -Infinity;
    let hasContent = false;

    for (let y = 0; y < originalHeight; y += 1) {
      for (let x = 0; x < originalWidth; x += 1) {
        const pixelIndex = (y * originalWidth + x) * 4;
        if ((layerImageData.data[pixelIndex + 3] || 0) > 0) {
          hasContent = true;
          minLocalX = Math.min(minLocalX, x * scale);
          minLocalY = Math.min(minLocalY, y * scale);
          maxLocalX = Math.max(maxLocalX, (x + 1) * scale);
          maxLocalY = Math.max(maxLocalY, (y + 1) * scale);
        }
      }
    }

    const layerWorldRect = {
      x: activeWorkspace.frameRect.x + layer.rect.x,
      y: activeWorkspace.frameRect.y + layer.rect.y,
      w: originalWidth * scale,
      h: originalHeight * scale,
    };
    const contentWorldRect = hasContent
      ? projectStudioLayerLocalRectToWorld(
          { x: minLocalX, y: minLocalY, w: maxLocalX - minLocalX, h: maxLocalY - minLocalY },
          layerWorldRect,
          resolveStudioLayerTransform(layer.params),
        )
      : null;

    if (!hasContent) {
      console.warn("zoomOnLayer: Layer has no visible content.");
      return;
    }

    const boundingBoxWidth = Math.max(0.00001, contentWorldRect?.w ?? 0);
    const boundingBoxHeight = Math.max(0.00001, contentWorldRect?.h ?? 0);

    const LayerRowRect: Rect = {
      x: contentWorldRect?.x ?? layerWorldRect.x,
      y: contentWorldRect?.y ?? layerWorldRect.y,
      w: boundingBoxWidth,
      h: boundingBoxHeight,
    };

    zoomToFitRect(projectId, LayerRowRect, 0.75);
  }, [kernel, zoomToFitRect]);


  const resizeGenerationAreaToLayerBBox = useCallback(async (layerOrId: Layer | string, ignoreTransparentBorder: boolean = false) => {
    if (!kernel.current) return;
    let layer: Layer | undefined;

    if (typeof layerOrId === 'string') {
      layer = kernel.current.getLayerRegistry()[layerOrId];
    } else {
      layer = layerOrId;
    }

    if (!layer) {
      console.warn("Layer not found.");
      return;
    }

    const workspaceId = layer.layerWorkspaceId;
    if (!workspaceId) return;
    const activeWorkspace = kernel.current.getWorkspace(workspaceId);
    if (!activeWorkspace) return;

    let minX: number, minY: number, maxX: number, maxY: number;

    if (!layer.imageData) {
      console.warn("Layer has no ImageData.");
      return;
    }
    const layerImageData = layer.imageData;
    const originalWidth = layerImageData.width;
    const originalHeight = layerImageData.height;
    const scale = resolveRasterLayerScale(layer);
    const scaledLayerWidth = originalWidth * scale;
    const scaledLayerHeight = originalHeight * scale;
    if (scaledLayerWidth <= 0 || scaledLayerHeight <= 0) {
      console.warn("Layer has zero dimensions after scaling.");
      return;
    }

    let localMinX = 0;
    let localMinY = 0;
    let localMaxX = scaledLayerWidth;
    let localMaxY = scaledLayerHeight;
    if (ignoreTransparentBorder) {
      localMinX = Infinity;
      localMinY = Infinity;
      localMaxX = -Infinity;
      localMaxY = -Infinity;
      for (let y = 0; y < originalHeight; y += 1) {
        for (let x = 0; x < originalWidth; x += 1) {
          const pixelIndex = (y * originalWidth + x) * 4;
          if ((layerImageData.data[pixelIndex + 3] || 0) > 0) {
            localMinX = Math.min(localMinX, x * scale);
            localMinY = Math.min(localMinY, y * scale);
            localMaxX = Math.max(localMaxX, (x + 1) * scale);
            localMaxY = Math.max(localMaxY, (y + 1) * scale);
          }
        }
      }
      if (!Number.isFinite(localMinX)) {
        console.warn("Layer has no visible content.");
        return;
      }
    }

    const layerWorldRect = {
      x: activeWorkspace.frameRect.x + layer.rect.x,
      y: activeWorkspace.frameRect.y + layer.rect.y,
      w: scaledLayerWidth,
      h: scaledLayerHeight,
    };
    const contentWorldRect = projectStudioLayerLocalRectToWorld(
      { x: localMinX, y: localMinY, w: localMaxX - localMinX, h: localMaxY - localMinY },
      layerWorldRect,
      resolveStudioLayerTransform(layer.params),
    );
    minX = contentWorldRect.x;
    minY = contentWorldRect.y;
    maxX = contentWorldRect.x + contentWorldRect.w;
    maxY = contentWorldRect.y + contentWorldRect.h;

    let boundingBoxWidth = Math.max(0.00001, maxX - minX);
    let boundingBoxHeight = Math.max(0.00001, maxY - minY);

    let dx = minX;
    let dy = minY;

    const aspectRatio = activeWorkspace.aspectRatio;
    if (aspectRatio) {
      const currentRatio = boundingBoxWidth / boundingBoxHeight;
      const centerX = minX + boundingBoxWidth / 2;
      const centerY = minY + boundingBoxHeight / 2;

      if (currentRatio > aspectRatio) {
        // Box is wider than target, need to increase height
        boundingBoxHeight = boundingBoxWidth / aspectRatio;
      } else {
        // Box is taller than or equal to target, need to increase width
        boundingBoxWidth = boundingBoxHeight * aspectRatio;
      }
      // Recalculate top-left corner to keep content centered
      dx = centerX - boundingBoxWidth / 2;
      dy = centerY - boundingBoxHeight / 2;
    }

    if (boundingBoxWidth <= 0 || boundingBoxHeight <= 0) {
      console.warn("Invalid bounding box dimensions.");
      return;
    }

    const generationRect = activeWorkspace.generationRect;
    const isSameBoundingBox = (
      boundingBoxWidth === generationRect.w &&
      boundingBoxHeight === generationRect.h &&
      dx === generationRect.x &&
      dy === generationRect.y
    );
    if (isSameBoundingBox && kernel.current?.canvasContainerRef.current) {
      zoomOnLayer(layer);
      return;
    }

    kernel.current?.setGenerationArea(workspaceId, dx, dy, boundingBoxWidth, boundingBoxHeight);
  }, [kernel, zoomOnLayer]);


  const resizeGenerationAreaToLayersUnion = useCallback(async (workspaceId: string, layerNamesOrIds: string[], marginFactor?: number) => {
    if (!kernel.current) {
      throw new Error("FRAME_LAYERS: kernel is not available.");
    }
    if (!layerNamesOrIds || layerNamesOrIds.length === 0) {
      throw new Error("FRAME_LAYERS: layerNamesOrIds parameter cannot be empty.");
    }

    const activeWorkspace = kernel.current.getWorkspace(workspaceId);

    if (!activeWorkspace) {
      throw new Error("FRAME_LAYERS: Active workspace not found.");
    }

    const layerTokens = new Set(
      layerNamesOrIds
        .map((value) => value.trim())
        .filter((value) => value.length > 0)
    );
    const layersInContext = kernel.current.getLayersInWorkspaceContext(workspaceId);
    const targetLayers = layersInContext.filter((l) =>
      (layerTokens.has(l.id) || layerTokens.has(l.name)) &&
      l.visible
    );

    if (targetLayers.length === 0) {
      throw new Error("FRAME_LAYERS: None of the specified layers found or are visible.");
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let hasContent = false;
    for (const layer of targetLayers) {
        if (layer.imageData) {
            hasContent = true;
            const worldSize = resolveRasterLayerWorldSize(layer);
            if (!worldSize) continue;
            const layerWorldRect = {
              x: activeWorkspace.frameRect.x + layer.rect.x,
              y: activeWorkspace.frameRect.y + layer.rect.y,
              w: worldSize.w,
              h: worldSize.h,
            };
            const transformedBounds = getTransformedRectBounds(
              layerWorldRect,
              resolveStudioLayerTransform(layer.params),
            );
            minX = Math.min(minX, transformedBounds.x);
            minY = Math.min(minY, transformedBounds.y);
            maxX = Math.max(maxX, transformedBounds.x + transformedBounds.w);
            maxY = Math.max(maxY, transformedBounds.y + transformedBounds.h);
        }
    }

    if (!hasContent) {
      throw new Error("FRAME_LAYERS: Could not determine bounding box for the specified layers (e.g., they have no content).");
    }
    
    const unionRect = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    const layersToFitForZoom = targetLayers;

    let newGenerationAreaX_world = unionRect.x;
    let newGenerationAreaY_world = unionRect.y;
    let newGenerationAreaWidth   = unionRect.w;
    let newGenerationAreaHeight  = unionRect.h;

    if (marginFactor && marginFactor > 1.0) {
      const centerX = newGenerationAreaX_world + newGenerationAreaWidth / 2;
      const centerY = newGenerationAreaY_world + newGenerationAreaHeight / 2;
      newGenerationAreaWidth *= marginFactor;
      newGenerationAreaHeight *= marginFactor;
      newGenerationAreaX_world = centerX - newGenerationAreaWidth / 2;
      newGenerationAreaY_world = centerY - newGenerationAreaHeight / 2;
    }

    const aspectRatio = activeWorkspace.aspectRatio;
    if (aspectRatio) {
      const currentRatio = newGenerationAreaWidth / newGenerationAreaHeight;
      const centerX = newGenerationAreaX_world + newGenerationAreaWidth / 2;
      const centerY = newGenerationAreaY_world + newGenerationAreaHeight / 2;

      if (currentRatio > aspectRatio) {
        // Box is wider than target, need to increase height
        newGenerationAreaHeight = newGenerationAreaWidth / aspectRatio;
      } else {
        // Box is taller than or equal to target, need to increase width
        newGenerationAreaWidth = newGenerationAreaHeight * aspectRatio;
      }
      newGenerationAreaX_world = centerX - newGenerationAreaWidth / 2;
      newGenerationAreaY_world = centerY - newGenerationAreaHeight / 2;
    }

    if (newGenerationAreaWidth <= 0 || newGenerationAreaHeight <= 0) {
      throw new Error("FRAME_LAYERS: Calculated invalid bounding box dimensions.");
    }

    const generationRect = activeWorkspace.generationRect;
    const oldGenerationAreaX_world = generationRect.x;
    const oldGenerationAreaY_world = generationRect.y;

    const frameRect = activeWorkspace.frameRect;

    const finalX = Math.max(frameRect.x, newGenerationAreaX_world);
    const finalY = Math.max(frameRect.y, newGenerationAreaY_world);
    const finalW = Math.min(newGenerationAreaWidth, (frameRect.x + frameRect.w) - finalX);
    const finalH = Math.min(newGenerationAreaHeight, (frameRect.y + frameRect.h) - finalY);

    const isSameBoundingBox = (
      Math.abs(finalW - generationRect.w) < 0.01 &&
      Math.abs(finalH - generationRect.h) < 0.01 &&
      Math.abs(finalX - oldGenerationAreaX_world) < 0.01 &&
      Math.abs(finalY - oldGenerationAreaY_world) < 0.01
    );

    if (isSameBoundingBox) {
      await kernel.current.zoomToFitAllLayers(workspaceId, layersToFitForZoom);
      return;
    }
    kernel.current.setGenerationArea(workspaceId, finalX, finalY, finalW, finalH);
  }, [kernel]);


  const downloadGenerationAreaAsImage = useCallback(async (
    workspaceId: string,
    megapixels: number = 6,
    format: OutputImageFormat = 'webp'
  ) => {
    if (!kernel.current) return;
    kernel.current.logUserAction('Background Image Downloaded', { megapixels: megapixels, format: format });

    const targetPixels = megapixels * 1000000;
    const { file: backgroundImageFile } = await renderWorkspaceArea(
      workspaceId,
      undefined,
      targetPixels,
      false,
      format !== 'jpeg',
      1,
      undefined,
      format
    );

    if (backgroundImageFile) {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(await signImageExport(backgroundImageFile));
      link.download = `fater.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  }, [kernel, renderWorkspaceArea]);


  const downloadWorkspaceView = useCallback(async (
    workspaceId: string,
    megapixels: number = 6,
    format: OutputImageFormat = 'webp'
  ) => {
    if (!kernel.current) return;
    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) {
        console.error(`downloadWorkspaceView: Workspace with ID "${workspaceId}" not found.`);
        return;
    }

    const layersToDownload = kernel.current.getLayersInWorkspaceContext(workspaceId).filter(l => l.visible);

    const layerIdsToDownload = layersToDownload.map(l => l.id);

    const rectToDownload = workspace.frameRect;

    if (layerIdsToDownload.length === 0) {
        console.warn(`downloadWorkspaceView: No visible layers found in workspace "${workspace.name}" or its parents.`);
        return;
    }

    kernel.current.logUserAction('Workspace View Downloaded', { workspaceId, megapixels, format });

    const targetPixels = megapixels * 1000000;
    const { file: imageFile } = await renderWorkspaceArea(
      workspaceId,
        layerIdsToDownload,
        targetPixels,
        false,
        format !== 'jpeg',
        1,
        rectToDownload,
        format,
        undefined
    );

    if (imageFile) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(await signImageExport(imageFile));
        link.download = `fater_${workspace.name || workspace.id}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }
  }, [kernel, renderWorkspaceArea]);


  const zoomOnAllFrames = useCallback((projectIdInput: string): boolean => {
    if (!kernel.current?.canvasContainerRef.current) {
        console.warn("zoomOnAllFrames: Missing canvas container ref. Cannot perform zoom.");
        return false;
    }

    const projectId = projectIdInput.trim();
    if (!projectId) {
      console.warn("zoomOnAllFrames: Missing projectId.");
      return false;
    }

    const visibleWorkspaces = listProjectWorkspaces(projectId).filter(ws => ws.visible ?? true);

    const allRects = [
      ...visibleWorkspaces.map(ws => ws.frameRect)
    ].filter((r): r is Rect => !!r);

    if (allRects.length === 0) {
      console.warn("zoomOnAllFrames: No frames found to zoom to.");
      return false;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    allRects.forEach(rect => {
      minX = Math.min(minX, rect.x);
      minY = Math.min(minY, rect.y);
      maxX = Math.max(maxX, rect.x + rect.w);
      maxY = Math.max(maxY, rect.y + rect.h);
    });

    // Expand maxY to include the version strips for each workspace.
    visibleWorkspaces.forEach(workspace => {
      const versionFilterMode = workspace.versionFilterMode || 'PRIMARY';
      const visibleVersions = kernel.current?.getVersionsForDisplay(workspace.id, versionFilterMode);
      const activeTasks = kernel.current?.getGenerationTasks(projectId).filter(task => task.workspaceId === workspace.id && !task.isOutcomeReported && !task.error);
      const totalItems = (visibleVersions?.length || 0) + (activeTasks?.length || 0);

      if (totalItems > 0) {
        // Replicate layout logic from our previous work
        const ITEM_WIDTH_PERCENT = 0.12;
        const GAP_REM = 0.5;
        const ROOT_FONT_SIZE_PX = 16;
        const GAP_PX = GAP_REM * ROOT_FONT_SIZE_PX;
        const TOP_MARGIN_PX = 8;

        const frameW = workspace.frameRect.w;
        const frameH = workspace.frameRect.h;
        if (frameW <= 0 || frameH <= 0) return;

        const itemWidth = frameW * ITEM_WIDTH_PERCENT;
        const frameAspectRatio = frameW / frameH;
        const itemHeight = itemWidth / frameAspectRatio;

        const itemsPerRow = Math.floor(frameW / (itemWidth + GAP_PX)) || 1;
        const numRows = Math.ceil(totalItems / itemsPerRow);
        const versionStripHeight = (numRows * itemHeight) + (Math.max(0, numRows - 1) * GAP_PX);

        const versionStripBottomY = workspace.frameRect.y + frameH + TOP_MARGIN_PX + versionStripHeight;
        
        maxY = Math.max(maxY, versionStripBottomY);
      }
    });

    const unionRect: Rect = {
      x: minX,
      y: minY,
      w: maxX - minX,
      h: maxY - minY,
    };

    if (unionRect.w <= 0 || unionRect.h <= 0) {
      console.warn("zoomOnAllFrames: Invalid union rect dimensions.");
      return false;
    }

    const canvasContainer = kernel.current.canvasContainerRef.current;
    const containerWidth = canvasContainer.offsetWidth;
    const containerHeight = canvasContainer.offsetHeight;
    if (!(containerWidth > 0 && containerHeight > 0)) return false;
    const zoomLevel = getProjectZoomLevel(kernel.current, projectId);
    const panOffset = getProjectPanOffset(kernel.current, projectId);

    // --- Safe Area Calculation ---
    const bottomBarHeight = 60;
    const aestheticMargin = 8;

    let paddingLeft = aestheticMargin;
    let paddingRight = aestheticMargin;
    let paddingTop = aestheticMargin;
    let paddingBottom = Math.max(aestheticMargin + bottomBarHeight, getTimelineDockBottomInsetPx(canvasContainer) + aestheticMargin);

    const availableWidth = containerWidth - paddingLeft - paddingRight;
    const availableHeight = containerHeight - paddingTop - paddingBottom;

    if (availableWidth <= 0 || availableHeight <= 0) return false;

    const targetZoomX = availableWidth / unionRect.w;
    const targetZoomY = availableHeight / unionRect.h;
    const newZoomLevel = Math.min(2048, Math.max(0.01, Math.min(targetZoomX, targetZoomY)));

    const rectCenterXWorld = unionRect.x + unionRect.w / 2;
    const rectCenterYWorld = unionRect.y + unionRect.h / 2;
    
    const availableCenterX = paddingLeft + availableWidth / 2;
    const availableCenterY = paddingTop + availableHeight / 2;

    const newPanX = availableCenterX - (rectCenterXWorld * newZoomLevel);
    const newPanY = availableCenterY - (rectCenterYWorld * newZoomLevel);

    const zoomThreshold = 0.001;
    const panThreshold = 1;

    if (
      Math.abs(newZoomLevel - zoomLevel) < zoomThreshold &&
      Math.abs(newPanX - panOffset.x) < panThreshold &&
      Math.abs(newPanY - panOffset.y) < panThreshold
    ) {
      return false;
    }

    kernel.current.animateZoomAndPan(projectId, newZoomLevel, { x: newPanX, y: newPanY }, 200);
    return true;

  }, [kernel, listProjectWorkspaces]);


  const animateGenerationArea = useCallback(async (workspaceId: string, target: Rect) => {
    if (!kernel.current) {
      throw new Error('animateGenerationArea: kernel is not available.');
    }
    const fromRect = kernel.current.getWorkspace(workspaceId)?.generationRect;
    if (!fromRect) {
      throw new Error(`animateGenerationArea: Workspace "${workspaceId}" has no generation area.`);
    }
    const animationProps: Rect = { ...fromRect };
    kernel.current.setWorkspaceAnimatedGenerationRect(workspaceId, { ...animationProps });
    try {
      // The final rect is applied before the glide, so the mask reprojection and any reader see it at once.
      await kernel.current.setGenerationArea(workspaceId, target.x, target.y, target.w, target.h);
      const appliedRect = kernel.current.getWorkspace(workspaceId)?.generationRect;
      if (!appliedRect) return;
      const delta =
        Math.abs(fromRect.x - appliedRect.x) +
        Math.abs(fromRect.y - appliedRect.y) +
        Math.abs(fromRect.w - appliedRect.w) +
        Math.abs(fromRect.h - appliedRect.h);
      if (delta < 0.01) return;
      kernel.current.playUISound('RESIZE', { volume: 0.4 });
      await anime({
        targets: animationProps,
        x: appliedRect.x,
        y: appliedRect.y,
        w: appliedRect.w,
        h: appliedRect.h,
        easing: 'easeInOutSine',
        duration: 600,
        update: () => {
          kernel.current?.setWorkspaceAnimatedGenerationRect(workspaceId, {
            x: animationProps.x,
            y: animationProps.y,
            w: animationProps.w,
            h: animationProps.h,
          });
        },
      }).finished;
    } finally {
      kernel.current?.setWorkspaceAnimatedGenerationRect(workspaceId, null);
    }
  }, [kernel]);

  const resizeGenerationAreaToWorkspace = useCallback(async (projectIdInput: string, workspaceId: string) => {
    if (!kernel.current) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;

    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) {
      console.error(`resizeGenerationAreaToWorkspace: Workspace with ID ${workspaceId} not found.`);
      return;
    }

    const { frameRect, generationRect } = workspace;
    const shouldAnimateSnap =
      kernel.current.getSpectacleMode() &&
      workspaceId === kernel.current.getProjectUIWorkspaceId(projectId);

    const fromRect = { ...generationRect };
    kernel.current.logUserAction('Generation Area Fit to Frame', { workspaceId });

    if (shouldAnimateSnap) {
      kernel.current.setWorkspaceAnimatedGenerationRect(workspaceId, fromRect);
    }

    try {
      await kernel.current.setGenerationArea(workspaceId, frameRect.x, frameRect.y, frameRect.w, frameRect.h);

      if (shouldAnimateSnap) {
        const appliedWorkspace = kernel.current.getWorkspace(workspaceId);
        const appliedRect = appliedWorkspace?.generationRect;

        if (appliedRect) {
          const delta =
            Math.abs(fromRect.x - appliedRect.x) +
            Math.abs(fromRect.y - appliedRect.y) +
            Math.abs(fromRect.w - appliedRect.w) +
            Math.abs(fromRect.h - appliedRect.h);

          if (delta > 0.01) {
            const animationProps = { ...fromRect };
            await anime({
              targets: animationProps,
              x: appliedRect.x,
              y: appliedRect.y,
              w: appliedRect.w,
              h: appliedRect.h,
              easing: 'easeInOutSine',
              duration: 220,
              update: () => {
                kernel.current?.setWorkspaceAnimatedGenerationRect(workspaceId, {
                  x: animationProps.x,
                  y: animationProps.y,
                  w: animationProps.w,
                  h: animationProps.h,
                });
              },
            }).finished;
          }
        }
      }
    } finally {
      if (shouldAnimateSnap) {
        kernel.current?.setWorkspaceAnimatedGenerationRect(workspaceId, null);
      }
    }
  }, [kernel]);

  const focusGenerationAreaForGridPanel = useCallback((
    projectIdInput: string,
    workspaceId: string,
    options?: {
      panelWidthPx?: number;
      panelGapPx?: number;
      viewportPaddingPx?: number;
      bottomOverlayPx?: number;
      marginFactor?: number;
    }
  ): boolean => {
    if (!kernel.current?.canvasContainerRef.current) return false;
    const projectId = projectIdInput.trim();
    if (!projectId) return false;

    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) return false;

    const ga = workspace.generationRect;
    if (!(ga.w > 0 && ga.h > 0)) return false;

    const canvasContainer = kernel.current.canvasContainerRef.current;
    const containerWidth = canvasContainer.offsetWidth;
    const containerHeight = canvasContainer.offsetHeight;
    if (!(containerWidth > 0 && containerHeight > 0)) return false;

    const viewportPaddingPx = Math.max(0, options?.viewportPaddingPx ?? 14);
    const panelWidthPx = Math.max(0, options?.panelWidthPx ?? 320);
    const panelGapPx = Math.max(0, options?.panelGapPx ?? 12);
    const bottomOverlayPx = Math.max(0, options?.bottomOverlayPx ?? 60, getTimelineDockBottomInsetPx(canvasContainer));
    const marginFactor = Math.max(0.5, options?.marginFactor ?? 1.03);

    const safeAreaLeft = viewportPaddingPx;
    const safeAreaTop = viewportPaddingPx;
    const safeAreaRight = viewportPaddingPx + panelGapPx + panelWidthPx + 18;
    const safeAreaBottom = viewportPaddingPx + bottomOverlayPx;

    const availableWidth = containerWidth - safeAreaLeft - safeAreaRight;
    const availableHeight = containerHeight - safeAreaTop - safeAreaBottom;
    if (!(availableWidth > 0 && availableHeight > 0)) return false;

    const targetZoomX = availableWidth / (ga.w * marginFactor);
    const targetZoomY = availableHeight / (ga.h * marginFactor);
    const targetZoom = Math.min(32, Math.max(0.01, Math.min(targetZoomX, targetZoomY)));

    const availableCenterX = safeAreaLeft + (availableWidth / 2);
    const availableCenterY = safeAreaTop + (availableHeight / 2);
    const gaCenterX = ga.x + (ga.w / 2);
    const gaCenterY = ga.y + (ga.h / 2);

    const targetPanX = availableCenterX - (gaCenterX * targetZoom);
    const targetPanY = availableCenterY - (gaCenterY * targetZoom);

    const currentZoom = getProjectZoomLevel(kernel.current, projectId);
    const currentPan = getProjectPanOffset(kernel.current, projectId);
    const zoomThreshold = 0.001;
    const panThreshold = 1;
    if (
      Math.abs(targetZoom - currentZoom) < zoomThreshold &&
      Math.abs(targetPanX - currentPan.x) < panThreshold &&
      Math.abs(targetPanY - currentPan.y) < panThreshold
    ) {
      return false;
    }

    kernel.current.animateZoomAndPan(projectId, targetZoom, { x: targetPanX, y: targetPanY }, 220);
    return true;
  }, [kernel]);

  const openWorkspaceColumnPick = useCallback((workspaceId: string, request: { purpose: 'seam' } | { purpose: 'develop'; recipeId: string }): boolean => {
    const k = kernel.current;
    if (!k) return false;
    const workspace = k.getWorkspace(workspaceId);
    if (!workspace) {
      k.handleError('This workspace no longer exists.');
      return false;
    }
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'openWorkspaceColumnPick');
    const layers = k.getLayersInWorkspaceContext(workspaceId);
    if (request.purpose === 'seam') {
      const bands = streetSeamBands(layers, workspace.frameRect);
      if (typeof bands === 'string') {
        k.handleError(bands);
        return false;
      }
      k.setWorkspaceColumnPick({ projectId, workspaceId, mode: 'single', purpose: 'seam' });
    } else {
      const recipe = STREET_DEVELOP_RECIPES.find((r) => r.id === request.recipeId);
      if (!recipe || !isStreetPanoramaFrame(workspace.frameRect)) {
        k.handleError(recipe ? 'This workspace is not a 360° panorama.' : `Unknown Develop 360 recipe "${request.recipeId}".`);
        return false;
      }
      k.setWorkspaceColumnPick({ projectId, workspaceId, mode: 'cuts', purpose: 'develop', recipeId: recipe.id, cuts: rememberedStreetCuts(layers) ?? uncutStreet() });
    }
    // One pick on the canvas at a time: both overlays take the whole canvas's clicks.
    k.setRecastZonePick(null);
    k.logUserAction('Workspace Column Pick Started', { workspaceId, purpose: request.purpose });
    return true;
  }, [kernel]);

  const updateWorkspaceColumnPick = useCallback((cuts: StreetCuts) => {
    if (!isStreetCuts(cuts)) return;
    kernel.current?.setWorkspaceColumnPick((pick) => (pick?.mode === 'cuts' ? { ...pick, cuts } : pick));
  }, [kernel]);

  const cancelWorkspaceColumnPick = useCallback(() => {
    kernel.current?.setWorkspaceColumnPick(null);
  }, [kernel]);

  const commitWorkspaceColumnPick = useCallback(async (fraction?: number): Promise<void> => {
    const k = kernel.current;
    const pick = k?.getWorkspaceColumnPick();
    if (!k || !pick) return;
    k.setWorkspaceColumnPick(null);
    try {
      if (pick.purpose === 'seam') {
        if (typeof fraction !== 'number') return;
        k.logUserAction('Street Seam Set', { workspaceId: pick.workspaceId, fraction });
        if (!await setStreetSeam(k, pick.workspaceId, fraction)) toast.info('The seam is already there: nothing was turned.');
        return;
      }
      k.logUserAction('Street Develop 360 Triggered', { workspaceId: pick.workspaceId, recipeId: pick.recipeId, slices: pick.cuts.at.length + 1 });
      rememberStreetCuts(k, pick.workspaceId, pick.cuts);
      await k.developStreet(pick.workspaceId, pick.recipeId, pick.cuts);
    } catch (error) {
      k.handleError(error instanceof Error ? error.message : 'The pick could not be applied.');
    }
  }, [kernel]);

  const openRecastZonePick = useCallback(async (workspaceId: string, detect = false): Promise<boolean> => {
    const k = kernel.current;
    if (!k) return false;
    if (!k.getWorkspace(workspaceId)) {
      k.handleError('This workspace no longer exists.');
      return false;
    }
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'openRecastZonePick');
    k.setWorkspaceColumnPick(null);
    const remembered = detect ? null : rememberedRecastZones(k.getLayersInWorkspaceContext(workspaceId));
    if (remembered) {
      k.setRecastZonePick({ projectId, workspaceId, zones: remembered, selected: null, detecting: null });
      k.logUserAction('Recast Zone Pick Started', { workspaceId, zones: remembered.length });
      return true;
    }
    // The pick opens at once, detecting: a cancel, another pick or a project switch replaces it, and the result is dropped.
    const detecting = crypto.randomUUID();
    const open = k.getRecastZonePick();
    k.setRecastZonePick(open?.workspaceId === workspaceId ? { ...open, selected: null, detecting } : { projectId, workspaceId, zones: [], selected: null, detecting });
    const isStillWanted = () => k.getRecastZonePick()?.detecting === detecting;
    let zones: RecastZone[];
    try {
      zones = await k.detectRecastZones(workspaceId);
    } catch (error) {
      if (!isStillWanted()) return false;
      k.setRecastZonePick((pick) => (pick && pick.zones.length > 0 ? { ...pick, detecting: null } : null));
      k.handleError(error instanceof Error ? error.message : 'People could not be detected.');
      return false;
    }
    if (!isStillWanted()) return false;
    k.setRecastZonePick((pick) => (pick ? { ...pick, zones, selected: null, detecting: null } : pick));
    if (!zones.length) toast.info('No one was found: drag on the frame to draw the zones to recast.');
    k.logUserAction('Recast Zone Pick Started', { workspaceId, zones: zones.length, detected: true });
    return true;
  }, [kernel]);

  const updateRecastZonePick = useCallback((zones: RecastZone[], selected: number | null) => {
    if (!isRecastZones(zones)) return;
    kernel.current?.setRecastZonePick((pick) => (pick && !pick.detecting ? { ...pick, zones, selected: selected !== null && selected < zones.length ? selected : null } : pick));
  }, [kernel]);

  const cancelRecastZonePick = useCallback(() => {
    kernel.current?.setRecastZonePick(null);
  }, [kernel]);

  const commitRecastZonePick = useCallback(async (): Promise<void> => {
    const k = kernel.current;
    const pick = k?.getRecastZonePick();
    if (!k || !pick || pick.detecting) return;
    k.setRecastZonePick(null);
    try {
      k.logUserAction('Recast Extras Triggered', { workspaceId: pick.workspaceId, zones: pick.zones.filter((zone) => zone.enabled).length });
      rememberRecastZones(k, pick.workspaceId, pick.zones);
      await k.recastExtras(pick.workspaceId, pick.zones);
    } catch (error) {
      k.handleError(error instanceof Error ? error.message : 'Recast extras could not run.');
    }
  }, [kernel]);

  return useMemo(() => ({
    handleCanvasKeyDown,
    downloadGenerationAreaAsImage,
    downloadWorkspaceView,
    extractLayersBoundingBox,
    zoomToFitAllLayers,
    getIdealLongestCanvasSize,
    renderWorkspaceArea,
    renderImageExport,
    renderWorkspaceAreaToNewLayer,
    zoomOnCanvas,
    fitCanvasToVisibleContentLayers,
    calculateFitToVisibleContentLayersRect,
    setGenerationArea,
    renderWorkspaceView,
    calculateAdjustGenerationAreaRelativeRect,
    zoomOnLayer,
    resizeGenerationAreaToLayerBBox,
    resizeGenerationAreaToLayersUnion,
    zoomOnAllFrames,
    animateGenerationArea,
    resizeGenerationAreaToWorkspace,
    focusGenerationAreaForGridPanel,
    openWorkspaceColumnPick,
    updateWorkspaceColumnPick,
    cancelWorkspaceColumnPick,
    commitWorkspaceColumnPick,
    openRecastZonePick,
    updateRecastZonePick,
    cancelRecastZonePick,
    commitRecastZonePick
  }), [
    handleCanvasKeyDown,
    downloadGenerationAreaAsImage,
    downloadWorkspaceView,
    extractLayersBoundingBox,
    zoomToFitAllLayers,
    getIdealLongestCanvasSize,
    renderWorkspaceArea,
    renderImageExport,
    renderWorkspaceAreaToNewLayer,
    zoomOnCanvas,
    fitCanvasToVisibleContentLayers,
    calculateFitToVisibleContentLayersRect,
    setGenerationArea,
    renderWorkspaceView,
    calculateAdjustGenerationAreaRelativeRect,
    zoomOnLayer,
    resizeGenerationAreaToLayerBBox,
    resizeGenerationAreaToLayersUnion,
    zoomOnAllFrames,
    animateGenerationArea,
    resizeGenerationAreaToWorkspace,
    focusGenerationAreaForGridPanel,
    openWorkspaceColumnPick,
    updateWorkspaceColumnPick,
    cancelWorkspaceColumnPick,
    commitWorkspaceColumnPick,
    openRecastZonePick,
    updateRecastZonePick,
    cancelRecastZonePick,
    commitRecastZonePick
  ]);
};
